/**
 * Tiered bundle — moteur d'affichage des prix remisés.
 *
 * Principe : chaque prix affiché est recalculé via addedCost(), avec
 * ré-application complète des règles Shopify (TB + CD), cheapest-first
 * allocation pour les quotas, max par unité pour la résolution. Deux modes
 * (settings.enable_tiered_bundle, cf. addedCost) :
 *   • 'marginal'      : ce que l'item AJOUTERAIT au panier
 *                       (computeTotal(state ∪ item) − computeTotal(state)) ;
 *   • 'product_price' : le prix de l'item seul après remise du palier atteint.
 *
 * Modes d'affichage :
 *
 *   • Single variant (data-tb-price-varies absent) :
 *     <span class="price__regular">prix</span>
 *     + strikethrough si compare_at > prix
 *     + badge mis à jour (data-tb-badge sur .badges[data-product-id]).
 *
 *   • Multi-variantes à prix différents (data-tb-price-varies="true") :
 *     <span class="price__regular price__regular--from">Dès <prix réduit></span>
 *     + strikethrough = compare_at de la variante au prix le plus bas
 *       (repli : price_min avant remise TB si pas de compare_at).
 *     Badge mis à jour avec « Jusqu'à -X% off ».
 *     Le <span class="price__discount-range"> qui contenait le « Jusqu'à »
 *     inline est masqué en .product-card (info redondante avec le badge).
 *
 *   • Items du bloc additionnel :
 *     – mode 'marginal'      : prix figé à la coche (lockedQueue),
 *       recalculé en ordre si un item antérieur est décoché ;
 *     – mode 'product_price' : tous les items cochés se mettent à jour
 *       ensemble selon le palier atteint (plus de figeage séquentiel).
 *
 * Coexistence CD : la logique CD est intégrée à computeTotal via
 * window.__conditionalDiscount.rules. Si CD est chargé séparément, il modifie
 * aussi le DOM mais TB le surcharge (chargé après dans theme.liquid). TB
 * supprime les badges CD ([data-cd-badge]) pour éviter les doublons.
 *
 * Pas de MutationObserver global (qui boucle avec les écritures TB et fige
 * la page) ; on s'appuie sur les événements pubsub / DOM ciblés.
 */

(function () {
  "use strict";

  const tbConfig = window.__tieredBundle;
  if (!tbConfig) return;

  // Mode d'affichage des prix (settings.enable_tiered_bundle) :
  // 'marginal' (défaut historique) ou 'product_price'. Voir addedCost().
  const priceMode =
    tbConfig.priceMode === "product_price" ? "product_price" : "marginal";

  const tbRules = (Array.isArray(tbConfig.rules) ? tbConfig.rules : [])
    .map((r) => ({
      productIdSet: new Set(r.productIds || []),
      // [CUSTOM] Un palier = un metaobject « level_tiered_bundle » (level_1 à
      // level_3) : quantité, %, gabarit de badge ([discount] = la réduction
      // affichée) et cadeaux (giftQty unités offertes parmi giftIdSet).
      tiers: (r.tiers || [])
        .slice()
        .sort((a, b) => a.qty - b.qty)
        .map((t) => ({
          qty: t.qty,
          pct: t.pct || 0,
          badge: t.badge || "",
          giftsBadge: t.giftsBadge || "",
          offer: t.offer || "",
          linkUrl: t.linkUrl || "",
          linkText: t.linkText || "",
          discountName: t.discountName || "",
          discountDisplayName: t.discountDisplayName || "",
          giftIdSet: new Set(t.giftIds || []),
          giftQty: t.giftQty || 0,
        })),
      // [CUSTOM] Dernier palier illimité : au-delà du quota du dernier palier,
      // la remise continue de s'appliquer (sinon on plafonne au quota).
      lastUnlimited: !!r.lastUnlimited,
      // [CUSTOM] Remise progressive : chaque palier s'applique à SA position
      // (produits classés par prix, plus cher = remise la plus faible), au lieu
      // d'un même % sur tous les éligibles. Voir computeUnitDiscounts.
      progressive: !!r.progressive,
    }))
    .filter((r) => r.productIdSet.size > 0 && r.tiers.length > 0);
  if (!tbRules.length) return;

  function getCdRules() {
    const cfg = window.__conditionalDiscount;
    if (!cfg || !Array.isArray(cfg.rules)) return [];
    return cfg.rules.map((r) => ({
      requiredProductIds: new Set(r.requiredProductIds || []),
      discountedProductIds: new Set(r.discountedProductIds || []),
      requiredQty: r.requiredQty || 1,
      discountedQty: r.discountedQty || 1,
      discountPct: r.discountPct || 0,
    }));
  }

  // ─── Formatage monétaire / markup ────────────────────────────────────────────

  function formatMoney(cents) {
    const fmt = theme?.settings?.currencyCodeEnabled
      ? theme?.settings?.moneyWithCurrencyFormat
      : theme?.settings?.moneyFormat;
    if (window.theme?.Currency?.formatMoney && fmt) {
      return theme.Currency.formatMoney(cents, fmt);
    }
    return (cents / 100).toFixed(2);
  }

  function fromTemplate(el) {
    return (
      el?.dataset?.tbFromTemplate ||
      window.theme?.strings?.tbFromTemplate ||
      window.theme?.strings?.tbFromMinTemplate ||
      "From __amount__"
    );
  }

  function upToTemplate(el) {
    return (
      el?.dataset?.tbUpToTemplate ||
      window.theme?.strings?.tbUpToTemplate ||
      window.theme?.strings?.upToOffTemplate ||
      "Up to __amount__ off"
    );
  }

  // Construit la valeur à injecter dans une template type « -__amount__% off »
  // de façon défensive : si la template porte déjà le « - » devant __amount__
  // (cas FR historique : « Jusqu'à -__amount__ »), on n'ajoute pas le nôtre.
  function pctValueForTemplate(tpl, pct) {
    const hasMinusInTpl = /-__amount__/.test(tpl);
    return (hasMinusInTpl ? "" : "-") + pct + "%";
  }

  function moneyValueForTemplate(tpl, cents) {
    const hasMinusInTpl = /-__amount__/.test(tpl);
    return (hasMinusInTpl ? "" : "-") + formatMoney(cents);
  }

  // [CUSTOM] Token du gabarit « badge » d'un palier : [discount]. L'ancienne
  // écriture {discount} reste acceptée pour les metafields déjà saisis.
  const TIER_DISCOUNT_TOKEN = /\[discount\]|\{discount\}/g;

  function discountStrForTierTpl(tpl, formatted) {
    const bare = String(formatted).replace(/^-/, "");
    if (/-\s*(?:\[discount\]|\{discount\})/.test(tpl)) {
      return bare;
    }
    return "-" + bare;
  }

  // Badge inline « -X% » / « Sale » pour usage dans les blocs sans .badges
  // (bundle items notamment). Format identique au badge natif Liquid.
  // productId optionnel : si fourni, applique le gabarit metafield « badge »
  // du palier ([discount]) comme applyTbBadge sur les cartes produit.
  function inlineBadgeHtml(price, comparePrice, productId, basePrice) {
    if (!tbConfig.saveAmountEnabled || !comparePrice || comparePrice <= price)
      return "";
    const cls =
      "badge badge--onsale flex items-center gap-1d5 font-medium leading-none rounded-full";
    let inner;
    if (price === 0 && tbConfig.freeDisplayMode === "price_free") {
      return "";
    }
    if (price === 0 && tbConfig.freeDisplayMode === "badge_free") {
      inner = tbConfig.i18n.free;
    } else if (tbConfig.saveType === "label") {
      inner = tbConfig.i18n.sale;
    } else if (tbConfig.saveType === "dollar") {
      const tpl = tbConfig.i18n.saveHtml || "Save __amount__";
      inner = tpl.replace(
        "__amount__",
        moneyValueForTemplate(tpl, comparePrice - price),
      );
    } else {
      const pct = Math.round(((comparePrice - price) * 100) / comparePrice);
      if (pct <= 0) return "";
      const tpl = tbConfig.i18n.saveHtml || "Save __amount__";
      inner = tpl.replace("__amount__", pctValueForTemplate(tpl, pct));
    }

    // [CUSTOM] Gabarit « badge » du palier ([discount]) — aligné sur les cartes.
    if (
      Number.isFinite(productId) &&
      inner &&
      tbConfig.saveType !== "label" &&
      !(price === 0 && tbConfig.freeDisplayMode === "badge_free")
    ) {
      const pct = Math.round(((comparePrice - price) * 100) / comparePrice);
      if (pct > 0) {
        const tierTpl = resolveTierBadgeTemplate(productId, pct, {
          basePrice: basePrice ?? price,
          displayed: price,
          comparePrice,
        });
        if (tierTpl) {
          const discountStr =
            tbConfig.saveType === "dollar"
              ? "-" + formatMoney(comparePrice - price)
              : "-" + pct + "%";
          inner = tierTpl.replace(TIER_DISCOUNT_TOKEN, discountStr);
        }
      }
    }

    return `<span class="${cls}">${inner}</span>`;
  }

  // compare_at de la variante au prix le plus bas (celle du « Dès … »).
  // Ne pas utiliser le min/max compare_at toutes variantes confondues.
  function getCheapestVariantCompareAt(productId, hintEl) {
    const variants = getProductVariants(productId, hintEl);
    if (!variants || variants.length === 0) return 0;
    let minPrice = Infinity;
    let bestCompare = 0;
    for (const v of variants) {
      const p = parseInt(v.price, 10) || 0;
      if (!(p > 0)) continue;
      const c = parseInt(v.compare_at_price, 10) || 0;
      if (p < minPrice) {
        minPrice = p;
        bestCompare = c > p ? c : 0;
      } else if (p === minPrice && c > p && c > bestCompare) {
        bestCompare = c;
      }
    }
    return bestCompare;
  }

  // Prix barré du mode « Dès » : compare_at de la plus petite variante
  // (prix non réduit Shopify). Repli = price_min avant remise TB si pas de
  // compare_at sur cette variante.
  function resolveFromCap(displayedPrice, basePrice, comparePrice, el) {
    let cap = parseInt(comparePrice, 10) || 0;
    if (!(cap > displayedPrice) && el) {
      const pid = parseInt(el.dataset?.productId, 10);
      if (Number.isFinite(pid)) {
        const fromVariants = getCheapestVariantCompareAt(pid, el);
        if (fromVariants > displayedPrice) cap = fromVariants;
      }
    }
    if (cap > displayedPrice) return cap;
    if (displayedPrice < basePrice) return basePrice;
    return 0;
  }

  // « Dès <prix> » [CUSTOM : + prix barré = compare_at de la plus petite
  // variante, sinon price_min si remise TB — cf. resolveFromCap].
  // includeUpTo + upToPct ajoutent un span .price__discount-range inline
  // (utilisé dans le bloc additionnel et les cartes horizontales, masqué via
  // CSS dans .product-card).
  function fromMarkup(price, el, includeUpTo, upToPct, cap) {
    const tpl = fromTemplate(el);
    const fromAmount =
      price === 0 && tbConfig.freeDisplayMode === "price_free"
        ? tbConfig.i18n.free
        : formatMoney(Math.max(0, price));
    const fromText = tpl.replace("__amount__", fromAmount);
    let html = `<span class="price__regular price__regular--from whitespace-nowrap">${fromText}</span>`;
    if (cap > price) {
      html +=
        `<span class="sr-only">${tbConfig.i18n.regularPriceLabel}</span>` +
        `<span class="price__sale inline-flex items-center h-auto relative">${formatMoney(cap)}</span>`;
    }
    if (includeUpTo && upToPct > 0) {
      const upTpl = upToTemplate(el);
      let upText = upTpl.replace(
        "__amount__",
        pctValueForTemplate(upTpl, upToPct),
      );
      // [CUSTOM] Gabarit metafield « badge » : [discount] = la phrase
      // « Jusqu'à -X% » entière ; le reste du texte (ex. PACK) vient du
      // metafield — même logique que applyTbBadge en fromMode.
      const pid = parseInt(el?.dataset?.productId, 10);
      if (Number.isFinite(pid)) {
        const tierTpl = lastBadgeTemplateForProduct(pid);
        if (tierTpl) {
          upText = tierTpl.replace(TIER_DISCOUNT_TOKEN, upText);
        }
      }
      html += `<span class="price__discount-range whitespace-nowrap">${upText}</span>`;
    }
    return html;
  }

  // Discount markup. includeBadge ajoute un badge inline (.badge--onsale)
  // — réservé aux contextes sans .badges container (bundle items).
  function regularPriceText(price) {
    if (price === 0 && tbConfig.freeDisplayMode === "price_free") {
      return tbConfig.i18n.free;
    }
    return formatMoney(price);
  }

  function regularPriceClass(price) {
    return price === 0 && tbConfig.freeDisplayMode === "price_free"
      ? "price__regular price__regular--free whitespace-nowrap"
      : "price__regular whitespace-nowrap";
  }

  function discountedMarkup(price, cap, includeBadge, productId, basePrice) {
    let html =
      `<span class="sr-only">${tbConfig.i18n.salePriceLabel}</span>` +
      `<span class="${regularPriceClass(price)}">${regularPriceText(price)}</span>` +
      `<span class="sr-only">${tbConfig.i18n.regularPriceLabel}</span>` +
      `<span class="price__sale inline-flex items-center h-auto relative">${formatMoney(cap)}</span>`;
    if (includeBadge)
      html += inlineBadgeHtml(price, cap, productId, basePrice ?? cap);
    return html;
  }

  function regularMarkup(price, compareAtPrice, includeBadge, productId) {
    const priceFmt = regularPriceText(price);
    const priceCls = regularPriceClass(price);
    if (compareAtPrice > price) {
      let html =
        `<span class="sr-only">${tbConfig.i18n.salePriceLabel}</span>` +
        `<span class="${priceCls}">${priceFmt}</span>` +
        `<span class="sr-only">${tbConfig.i18n.regularPriceLabel}</span>` +
        `<span class="price__sale inline-flex items-center h-auto relative">${formatMoney(compareAtPrice)}</span>`;
      if (includeBadge) html += inlineBadgeHtml(price, compareAtPrice, productId);
      return html;
    }
    return `<span class="${priceCls}">${priceFmt}</span>`;
  }

  // Trouve la product-info associée à un élément, même quand l'élément est
  // HORS du <product-info> (cas du sticky, qui est sibling de product-info
  // mais dans la même <section>). Recherche d'abord en ancêtre direct, puis
  // dans la section/scope englobant via data-product-id.
  function getScopeForProductInfo(productInfo) {
    if (!productInfo) return document;
    return (
      productInfo.closest(".quick-view, .x-modal") ||
      productInfo.closest(".featured-product") ||
      productInfo.closest(".shopify-section, section") ||
      document
    );
  }

  function findProductInfoFor(el) {
    if (!el || !el.closest) return null;
    let pi = el.closest("product-info");
    if (pi) return pi;
    const pid =
      el.dataset?.productId ||
      el.getAttribute?.("data-product-id") ||
      el.closest?.("[data-product-id]")?.dataset?.productId;
    if (!pid) return null;
    const scope =
      el.closest(
        ".quick-view, .x-modal, .featured-product, .shopify-section, section",
      ) || document;
    return scope.querySelector(`product-info[data-product-id="${pid}"]`);
  }

  // custom_preselect-variant.js : l'utilisateur doit cliquer chaque option au
  // moins une fois avant que la sélection soit considérée « complète ».
  function isVariantSelectionPending(productInfo) {
    if (!productInfo) return false;
    const container = productInfo.closest("[data-validation-initialized]");
    if (!container) return false;
    return container.getAttribute("data-variant-selection-complete") !== "true";
  }

  function getVariantPickerOptionCount(variantPicker) {
    if (!variantPicker) return 0;
    return variantPicker.querySelectorAll("fieldset.product-form__input")
      .length;
  }

  // Variante entièrement sélectionnée (toutes les options du picker).
  function hasCompleteVariantSelection(productInfo) {
    const variantPicker = productInfo?.querySelector("variant-picker");
    if (!variantPicker) return true;
    const totalOptions = getVariantPickerOptionCount(variantPicker);
    if (totalOptions === 0) return true;

    const checkedInputs = variantPicker.querySelectorAll(
      'input[type="radio"]:checked',
    );
    if (checkedInputs.length >= totalOptions) return true;

    const selects = variantPicker.querySelectorAll("select");
    let filledSelects = 0;
    selects.forEach((sel) => {
      if (sel.value !== "" && sel.value != null) filledSelects++;
    });
    return checkedInputs.length + filledSelects >= totalOptions;
  }

  function shouldShowVariantPreview(productInfo) {
    if (!productInfo) return false;
    if (isVariantSelectionPending(productInfo)) return true;
    return !hasCompleteVariantSelection(productInfo);
  }

  function hasSamePriceVariantSale(variants) {
    if (!variants || variants.length < 2) return false;
    const prices = variants.filter((v) => v.price > 0).map((v) => v.price);
    if (prices.length === 0) return false;
    if (Math.min(...prices) !== Math.max(...prices)) return false;
    return variants.some(
      (v) => v.compare_at_price && v.price && v.compare_at_price > v.price,
    );
  }

  function getSamePriceSalePreviewData(variants) {
    if (!hasSamePriceVariantSale(variants)) return null;
    const pricedVariants = variants.filter((v) => v.price > 0);
    const minPrice = Math.min(...pricedVariants.map((v) => v.price));
    let maxCompareAt = 0;
    pricedVariants.forEach((v) => {
      if (
        v.price === minPrice &&
        v.compare_at_price > v.price &&
        v.compare_at_price > maxCompareAt
      ) {
        maxCompareAt = v.compare_at_price;
      }
    });
    if (maxCompareAt <= minPrice) return null;
    return { minPrice, maxCompareAt };
  }

  // Mode « Dès … » / aperçu :
  //  • Tant que la validation variante n'est pas terminée (custom_preselect)
  //    ou qu'il manque des options → mode aperçu (« Jusqu'à … » ou promo max).
  //  • Après sélection complète d'une variante pour CE produit → prix/badge
  //    spécifiques à la variante (comme sans tiered bundle).
  //  • Le test productId évite que les bundle items suivent l'état de la fiche.
  function isFromMode(el) {
    if (!el) return false;
    const pi = findProductInfoFor(el);
    const elPid = el.dataset?.productId;
    const piPid = pi?.dataset?.productId;
    const sameProduct = !elPid || !piPid || String(elPid) === String(piPid);

    if (pi && sameProduct && shouldShowVariantPreview(pi)) {
      const variants = getProductVariants(
        parseInt(pi.dataset.productId, 10),
        el,
      );
      if (hasSamePriceVariantSale(variants)) return false;
      if (el.dataset.tbPriceVaries === "true") return true;
      if (el.querySelector(".price__regular--from")) return true;
      if (el.dataset.priceMax && el.dataset.priceMin) {
        const max = parseInt(el.dataset.priceMax, 10);
        const min = parseInt(el.dataset.priceMin, 10);
        if (Number.isFinite(max) && Number.isFinite(min) && max > min)
          return true;
      }
      return false;
    }

    if (pi && sameProduct && hasCompleteVariantSelection(pi)) return false;
    if (el.dataset.tbVariantSelected === "true") return false;
    if (el.dataset.tbPriceVaries === "true") return true;
    if (el.querySelector(".price__regular--from")) return true;
    if (el.dataset.priceMax && el.dataset.priceMin) {
      const max = parseInt(el.dataset.priceMax, 10);
      const min = parseInt(el.dataset.priceMin, 10);
      if (Number.isFinite(max) && Number.isFinite(min) && max > min)
        return true;
    }
    return false;
  }

  // ─── Badges ──────────────────────────────────────────────────────────────────

  // Texte du badge de réduction variante (compare_at), sans gabarit tier « PACK ».
  function compareSaleBadgeInner(badgesEl, basePrice, comparePrice) {
    if (!comparePrice || comparePrice <= basePrice) return null;
    const saveType =
      badgesEl.dataset.badgeSaveType || tbConfig.saveType || "percent";
    if (saveType === "label") {
      return badgesEl.dataset.badgeSaleLabel || tbConfig.i18n.sale;
    }
    if (saveType === "dollar") {
      const tpl =
        badgesEl.dataset.badgeSaveTemplate ||
        tbConfig.i18n.saveHtml ||
        "Save __amount__";
      return tpl.replace(
        "__amount__",
        moneyValueForTemplate(tpl, comparePrice - basePrice),
      );
    }
    const pct = Math.round(((comparePrice - basePrice) * 100) / comparePrice);
    if (pct <= 0) return null;
    const tpl =
      badgesEl.dataset.badgeSaveTemplate ||
      tbConfig.i18n.saveHtml ||
      "Save __amount__";
    return tpl.replace("__amount__", pctValueForTemplate(tpl, pct));
  }

  function syncCompareSaleBadge(badgesEl, basePrice, comparePrice) {
    const inner = compareSaleBadgeInner(badgesEl, basePrice, comparePrice);
    if (!inner) return;
    const existing = badgesEl.querySelectorAll(
      ".badge--onsale:not([data-tb-badge]):not([data-cd-badge])",
    );
    if (existing.length > 0) {
      existing.forEach((b) => {
        b.innerHTML = inner;
        b.style.display = "";
      });
      return;
    }
    const isVert = !!badgesEl.querySelector(".badge--vertical");
    const cls = `badge${isVert ? " badge--vertical" : ""} badge--onsale flex items-center gap-1d5 font-medium leading-none rounded-full`;
    const span = document.createElement("span");
    span.className = cls;
    span.innerHTML = inner;
    badgesEl.appendChild(span);
  }

  // Met à jour les .badges[data-product-id="..."] pour refléter la remise TB.
  // scopeRoot limite la mise à jour au modal / fiche concerné (évite la désync
  // fiche + quick-view pour le même product-id).
  function applyTbBadge(
    productId,
    displayed,
    basePrice,
    comparePrice,
    fromMode,
    upToPct,
    scopeRoot,
    cartSavings,
  ) {
    const root = scopeRoot && scopeRoot.querySelectorAll ? scopeRoot : document;
    root
      .querySelectorAll(`.badges[data-product-id="${productId}"]`)
      .forEach((badgesEl) => {
        badgesEl
          .querySelectorAll(
            "[data-tb-badge], [data-cd-badge], [data-variant-validation-badge]",
          )
          .forEach((b) => b.remove());

        const nativeBadges = badgesEl.querySelectorAll(
          ".badge--onsale:not([data-tb-badge]):not([data-cd-badge])",
        );
        nativeBadges.forEach((b) => {
          b.style.display = "";
        });

        const tbAffects = displayed < basePrice;
        const onCompareSale = comparePrice > basePrice;

        // [CUSTOM] Badge « économie panier » (progressif) : le produit n'a PAS de
        // remise propre (il devient le produit le plus cher = 1re position) mais
        // son ajout remise les autres produits du panier. On affiche alors le
        // gabarit « badge » avec [discount] → l'économie TOTALE en devise sur le
        // panier. Prioritaire sur l'absence de badge.
        if (!tbAffects && !fromMode && cartSavings > 0 && tbConfig.saveAmountEnabled) {
          const savingsTpl = badgeTemplateForProduct(productId, 0) ||
            firstBadgeTemplateFor(productId);
          if (savingsTpl) {
            // Variante en promo : badge classique (-X% / montant) puis highlight PACK
            // (même paire que le cart drawer / bundle collection).
            if (onCompareSale) {
              syncCompareSaleBadge(badgesEl, basePrice, comparePrice);
            } else {
              nativeBadges.forEach((b) => {
                b.style.display = "none";
              });
            }
            const inner = savingsTpl.replace(
              TIER_DISCOUNT_TOKEN,
              discountStrForTierTpl(savingsTpl, formatMoney(cartSavings)),
            );
            // Badge « économie panier » en devise → badge--highlight (pas onsale).
            const isVert = !!badgesEl.querySelector(".badge--vertical");
            const cls = `badge${isVert ? " badge--vertical" : ""} badge--highlight flex items-center gap-1d5 font-medium leading-none rounded-full`;
            const tmp = document.createElement("div");
            tmp.innerHTML = `<span class="${cls}">${inner}</span>`;
            const badge = tmp.firstElementChild;
            if (badge) {
              badge.dataset.tbBadge = "1";
              badgesEl.appendChild(badge);
            }
          }
          return;
        }

        if (!tbAffects && !fromMode && !onCompareSale) return;
        if (!tbConfig.saveAmountEnabled) return;

        // Mode « Offert » à la place du prix : aucun badge (natif inclus)
        if (displayed === 0 && tbConfig.freeDisplayMode === "price_free") {
          nativeBadges.forEach((b) => {
            b.style.display = "none";
          });
          return;
        }

        let inner = null;
        if (displayed === 0 && tbConfig.freeDisplayMode === "badge_free") {
          inner = tbConfig.i18n.free;
        } else if (tbConfig.saveType === "label") {
          inner = tbConfig.i18n.sale;
        } else {
          let pct;
          if (fromMode) {
            pct = upToPct;
          } else {
            const ref = comparePrice > basePrice ? comparePrice : basePrice;
            pct = ref > 0 ? Math.round(((ref - displayed) * 100) / ref) : 0;
            if (pct <= 0 && comparePrice > displayed) {
              pct = Math.round(
                ((comparePrice - displayed) * 100) / comparePrice,
              );
            }
          }
          if (pct <= 0) return;

          let tpl;
          if (fromMode) {
            tpl =
              badgesEl.dataset.badgeUpToTemplate ||
              window.theme?.strings?.upToOffTemplate ||
              "Up to __amount__ off";
          } else if (tbConfig.saveType === "dollar") {
            const ref = comparePrice > basePrice ? comparePrice : basePrice;
            tpl =
              badgesEl.dataset.badgeSaveTemplate ||
              tbConfig.i18n.saveHtml ||
              "Save __amount__";
            inner = tpl.replace(
              "__amount__",
              moneyValueForTemplate(tpl, ref - displayed),
            );
          } else {
            tpl =
              badgesEl.dataset.badgeSaveTemplate ||
              tbConfig.i18n.saveHtml ||
              "Save __amount__";
          }
          if (inner == null) {
            inner = tpl.replace("__amount__", pctValueForTemplate(tpl, pct));
          }

          // [CUSTOM] Gabarit de badge par palier (clé « badge » du metafield) :
          // remplace le contenu par le gabarit tel quel ; [discount] = la
          // réduction affichée. Badge normal → « -X% » / montant. Mode
          // « Jusqu'à » → la phrase entière « Jusqu'à -X% » (le texte autour,
          // ex. « PACK », vient uniquement du metafield).
          // Lookup fromMode : dernier palier avec un gabarit (le % recalculé
          // ne matche presque jamais un tier.pct exact).
          const tierTpl = resolveTierBadgeTemplate(productId, pct, {
            fromMode,
            basePrice,
            displayed,
            comparePrice,
          });
          if (tierTpl) {
            let discountStr;
            if (fromMode) {
              discountStr = inner;
            } else {
              const ref = comparePrice > basePrice ? comparePrice : basePrice;
              discountStr =
                tbConfig.saveType === "dollar"
                  ? "-" + formatMoney(ref - displayed)
                  : "-" + pct + "%";
            }
            inner = tierTpl.replace(TIER_DISCOUNT_TOKEN, discountStr);
          }
        }

        if (!inner) return;

        nativeBadges.forEach((b) => {
          b.style.display = "none";
        });

        const isVert = !!badgesEl.querySelector(".badge--vertical");
        const cls = `badge${isVert ? " badge--vertical" : ""} badge--onsale flex items-center gap-1d5 font-medium leading-none rounded-full`;
        const tmp = document.createElement("div");
        tmp.innerHTML = `<span class="${cls}">${inner}</span>`;
        const badge = tmp.firstElementChild;
        if (badge) {
          badge.dataset.tbBadge = "1";
          badgesEl.appendChild(badge);
        }
      });
  }

  // ─── Variantes (data-product-variant-data) ──────────────────────────────────

  // Map productId → array de variantes (price, compare_at_price).
  // Priorité : data-tb-variants sur l'élément (disponible sur les cartes où
  // il n'y a pas de product-info). Sinon, script[data-product-variant-data]
  // dans product-info (fiche/modal).
  function getProductVariants(productId, hintEl) {
    if (hintEl) {
      const raw = hintEl.dataset && hintEl.dataset.tbVariants;
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            return parsed.map((v) => ({
              price: v.p,
              compare_at_price: v.c,
            }));
          }
        } catch (_) {
          /* ignore */
        }
      }
    }
    const candidates = document.querySelectorAll(
      `product-info[data-product-id="${productId}"]`,
    );
    for (const pi of candidates) {
      const scriptTag = pi.querySelector("script[data-product-variant-data]");
      if (!scriptTag) continue;
      try {
        const data = JSON.parse(scriptTag.textContent);
        if (Array.isArray(data.productVariants)) return data.productVariants;
      } catch (_) {
        /* ignore */
      }
    }
    return null;
  }

  // Calcule le pourcentage MAX de remise observable parmi toutes les variantes
  // d'un produit (en réappliquant TB + CD pour chacune).
  // stateUnits : ÉTAT de base auquel on ajoute hypothétiquement chaque variante.
  //   • Cards / fiche / modal : panier seul.
  //   • Bundle items non cochés : panier + page (qty) + cochés (= baseStateUnits).
  // hintEl : élément .price (ou bundle-price) pour lire data-tb-variants en
  //   fallback du script[data-product-variant-data] des cartes.
  function computeMaxDiscountAcrossVariants(
    productId,
    stateUnits,
    cdRules,
    hintEl,
    applyPack,
  ) {
    const variants = getProductVariants(productId, hintEl);
    if (!variants || variants.length === 0) return 0;
    let maxPct = 0;
    for (const v of variants) {
      const variantPrice = parseInt(v.price, 10);
      const compareAtPrice = parseInt(v.compare_at_price, 10) || 0;
      if (!Number.isFinite(variantPrice) || variantPrice <= 0) continue;
      const withV = [
        ...stateUnits,
        { productId, basePrice: variantPrice, comparePrice: compareAtPrice },
      ];
      // Coût marginal capé au prix de la variante : si l'ajout déplace une
      // remise sur un item plus cher, le pct effectif peut être nul (capé).
      const net = cappedMarginal(
        stateUnits,
        withV,
        variantPrice,
        cdRules,
        applyPack,
      );
      const ref = compareAtPrice > variantPrice ? compareAtPrice : variantPrice;
      if (ref <= 0) continue;
      const pct = ((ref - net) * 100) / ref;
      if (pct > maxPct) maxPct = pct;
    }
    return Math.round(maxPct);
  }

  // ─── Helpers tier ────────────────────────────────────────────────────────────

  function tierPctFor(qty, tiers) {
    let pct = 0;
    for (const t of tiers) {
      if (qty >= t.qty) pct = t.pct;
      else break;
    }
    return pct;
  }

  // [CUSTOM] Cadeaux : paliers FRANCHIS accordant des cadeaux pour une quantité
  // éligible. Les cadeaux se CUMULENT d'un palier à l'autre (palier 1 :
  // 1 cadeau, palier 2 : 2 cadeaux → 3 unités offertes au palier 2).
  function giftGrantsFor(rule, qty) {
    const grants = [];
    for (const tier of rule.tiers) {
      if (qty < tier.qty) break;
      if (tier.giftQty > 0 && tier.giftIdSet.size > 0) {
        grants.push({
          giftIdSet: tier.giftIdSet,
          qty: tier.giftQty,
          giftsBadge: tier.giftsBadge,
        });
      }
    }
    return grants;
  }

  // [CUSTOM] Applique les cadeaux d'une règle : remise de 100 % sur le nombre
  // d'unités accordé, prises parmi les produits offerts du palier (clés
  // gift_products / gift_collections). Même ordre d'attribution que les remises
  // de palier (« Lowest priced first », les unités en cours d'ajout en dernier —
  // cf. compareForAllocation). Le thème n'ajoute rien au panier : un cadeau est
  // simplement une unité affichée à -100 %, le reste du rendu (prix, badges,
  // « Offert ») est celui de n'importe quelle remise.
  function allocateGifts(u, rule, qty) {
    for (const grant of giftGrantsFor(rule, qty)) {
      const eligible = u.filter(
        (it) =>
          !it.isFixed && grant.giftIdSet.has(it.productId) && it.disc < 100,
      );
      if (!eligible.length) continue;
      eligible.sort(compareForAllocation);
      const slots = Math.min(grant.qty, eligible.length);
      for (let i = 0; i < slots; i++) eligible[i].disc = 100;
    }
  }

  // ─── Badge « cadeau » (gifts_badge) ─────────────────────────────────────────
  //
  // Affiché sur les cartes des produits OFFRABLES (gift_products /
  // gift_collections d'un palier), PARTOUT sur le site, dès que le PANIER RÉEL
  // a atteint un palier accordant un cadeau. Indépendant du prix affiché (le
  // produit candidat n'est pas forcément déjà au panier) : passe dédiée sur
  // tous les conteneurs de badges du DOM, réévaluée à chaque cycle (mêmes
  // événements que les prix — cart:updated, collection:reloaded, etc.).

  function giftBadgeMarkup(text, isVert) {
    const span = document.createElement("span");
    span.className = `badge${isVert ? " badge--vertical" : ""} badge--highlight flex items-center gap-1d5 font-medium leading-none rounded-full`;
    span.dataset.tbGiftBadge = "1";
    span.textContent = text;
    return span;
  }

  // productId offrable → texte du gifts_badge du palier qui l'accorde (panier
  // RÉEL uniquement, cf. cartUnits).
  function computeGiftBadgeTextByProduct(cartUnits) {
    const textByProduct = new Map();
    tbRules.forEach((rule) => {
      let qty = 0;
      for (const u of cartUnits) if (rule.productIdSet.has(u.productId)) qty++;
      giftGrantsFor(rule, qty).forEach((grant) => {
        if (!grant.giftsBadge) return;
        grant.giftIdSet.forEach((pid) => {
          if (!textByProduct.has(pid)) textByProduct.set(pid, grant.giftsBadge);
        });
      });
    });
    return textByProduct;
  }

  function syncGiftBadge(container, productId, text) {
    const existing = container.querySelector("[data-tb-gift-badge]");
    if (!text) {
      if (existing) existing.remove();
      return;
    }
    if (existing) {
      if (existing.textContent !== text) existing.textContent = text;
      return;
    }
    const isVert = !!container.querySelector(".badge--vertical");
    container.appendChild(giftBadgeMarkup(text, isVert));
  }

  // [CUSTOM] Portée volontairement restreinte : PAS sur les produits déjà au
  // panier (la ligne panier elle-même), UNIQUEMENT sur les blocs « produits
  // complémentaires » et « récemment vus » (partout où ils apparaissent : fiche
  // produit, cart drawer, etc.) — ce sont des blocs de suggestion, le badge y a
  // un rôle d'incitation qui n'a pas de sens sur un produit déjà acquis.
  const GIFT_BADGE_SCOPE_SELECTOR =
    ".complementary-products .badges[data-product-id], recently-viewed .badges[data-product-id]";

  function applyGiftBadges(cartUnits) {
    const textByProduct = computeGiftBadgeTextByProduct(cartUnits);

    // Nettoyage limité à NOS conteneurs : la grille « Vos cadeaux » de la page
    // bundle pose ses propres [data-tb-gift-badge] (collection-bundle.js,
    // applyGiftsGridBadges) — un nettoyage global les effacerait à chaque cycle.
    document
      .querySelectorAll(
        ".complementary-products [data-tb-gift-badge], recently-viewed [data-tb-gift-badge]",
      )
      .forEach((b) => b.remove());

    document.querySelectorAll(GIFT_BADGE_SCOPE_SELECTOR).forEach((badgesEl) => {
      const pid = parseInt(badgesEl.dataset.productId, 10);
      syncGiftBadge(badgesEl, pid, textByProduct.get(pid));
    });
  }

  // [CUSTOM] Gabarit de badge (clé « badge » du palier) pour un produit dont la
  // remise affichée vaut pct. On repère la règle qui couvre le produit et le
  // palier dont le pct correspond, puis on renvoie son gabarit. null si aucun
  // gabarit défini → badge par défaut.
  function badgeTemplateForProduct(productId, pct) {
    for (const rule of tbRules) {
      if (!rule.productIdSet.has(productId)) continue;
      for (const tier of rule.tiers) {
        if (tier.pct === pct && tier.badge) return tier.badge;
      }
    }
    return null;
  }

  // [CUSTOM] Gabarit « badge » (1er palier) d'une règle PROGRESSIVE couvrant le
  // produit — support du badge « économie panier » (produit sans remise propre).
  function firstBadgeTemplateFor(productId) {
    for (const rule of tbRules) {
      if (!rule.progressive) continue;
      if (!rule.productIdSet.has(productId)) continue;
      if (rule.tiers[0] && rule.tiers[0].badge) return rule.tiers[0].badge;
    }
    return null;
  }

  // [CUSTOM] Gabarit « badge » du palier le plus profond qui en définit un —
  // pour le mode « Jusqu'à » : le % affiché est recalculé et ne matche presque
  // jamais un tier.pct exact. On applique le gabarit metafield tel quel
  // ([discount] = « Jusqu'à -X% »).
  function lastBadgeTemplateForProduct(productId) {
    for (const rule of tbRules) {
      if (!rule.productIdSet.has(productId)) continue;
      for (let i = rule.tiers.length - 1; i >= 0; i--) {
        if (rule.tiers[i].badge) return rule.tiers[i].badge;
      }
    }
    return null;
  }

  // [CUSTOM] Résout le gabarit « badge » d'un palier. Variante déjà en promo
  // (compare_at) + remise pack : le % cumulé ne matche pas tier.pct — repli sur
  // le palier TB actif puis le dernier gabarit défini (comme en mode « Jusqu'à »).
  function resolveTierBadgeTemplate(productId, displayPct, opts = {}) {
    if (opts.fromMode) return lastBadgeTemplateForProduct(productId);

    const tpl = badgeTemplateForProduct(productId, displayPct);
    if (tpl) return tpl;

    const basePrice = opts.basePrice;
    const displayed = opts.displayed;
    const comparePrice = opts.comparePrice || 0;
    const onCompareSale =
      Number.isFinite(basePrice) &&
      Number.isFinite(comparePrice) &&
      comparePrice > basePrice;
    const tbAffects =
      Number.isFinite(basePrice) &&
      Number.isFinite(displayed) &&
      displayed < basePrice;

    if (onCompareSale && tbAffects && basePrice > 0) {
      const tbPct = Math.round(((basePrice - displayed) * 100) / basePrice);
      if (tbPct > 0) {
        const byTb = badgeTemplateForProduct(productId, tbPct);
        if (byTb) return byTb;
      }
      return lastBadgeTemplateForProduct(productId);
    }

    return null;
  }

  // ─── État panier ─────────────────────────────────────────────────────────────

  let cartData = tbConfig.cartData || null;

  function getCartUnits() {
    if (!cartData) return [];
    const out = [];
    for (const it of cartData.items) {
      for (let i = 0; i < it.quantity; i++) {
        out.push({
          productId: it.product_id,
          basePrice: it.price || 0,
          comparePrice: it.compare_at_price || 0,
        });
      }
    }
    return out;
  }

  // [CUSTOM] Unités du pack en construction sur la page collection bundle
  // (publiées par collection-bundle.js via window.__collectionBundlePackUnits).
  // Ajoutées au panier pour l'aperçu prix/réduction des cartes de la grille et
  // du modal « choisir les options » — PAS du quick view « œil » (cf.
  // contextIncludesPack). Vide hors page bundle → sans effet ailleurs.
  function getBundlePackUnits() {
    const raw = window.__collectionBundlePackUnits;
    if (!Array.isArray(raw)) return [];
    const out = [];
    for (const u of raw) {
      const basePrice = parseInt(u.basePrice, 10);
      if (!Number.isFinite(basePrice) || basePrice <= 0) continue;
      out.push({
        productId: u.productId,
        basePrice,
        comparePrice: parseInt(u.comparePrice, 10) || 0,
      });
    }
    return out;
  }

  // Un contexte product-info doit-il inclure les unités du pack ?
  // Non pour le quick view « œil » (quick-view SANS data-bundle-controls) :
  // il n'affiche que l'effet du panier. Oui partout ailleurs (modal « choisir
  // les options » qui porte data-bundle-controls, fiche produit, etc.).
  function contextIncludesPack(ctx) {
    const qv = ctx.productInfo && ctx.productInfo.closest
      ? ctx.productInfo.closest("quick-view")
      : null;
    if (qv && !qv.hasAttribute("data-bundle-controls")) return false;
    return true;
  }

  // Construit la liste des contextes « page » : un par product-info présent
  // dans le DOM (fiche produit + chaque modal ouvert). Chaque contexte a son
  // propre scope, priceEl, qty selector — ce qui permet au sticky / ATC du
  // modal d'utiliser le marginal calculé avec la qty du modal (pas celle de
  // la fiche).
  function getPageContexts() {
    const contexts = [];
    document.querySelectorAll("product-info[data-product-id]").forEach((pi) => {
      const pid = parseInt(pi.dataset.productId, 10);
      if (!Number.isFinite(pid)) return;
      const scope = getScopeForProductInfo(pi);
      const root = scope === document ? document : scope;
      const priceEl = root.querySelector(
        `.price[data-product-id="${pid}"][data-base-price]:not([data-bundle-price]):not([data-bundle-pack-expand])`,
      );
      if (!priceEl) return;
      const basePrice = parseInt(priceEl.dataset.basePrice, 10);
      const comparePrice =
        parseInt(priceEl.dataset.baseCompareAtPrice, 10) || 0;
      if (!Number.isFinite(basePrice) || basePrice <= 0) return;
      const qtyInput = root.querySelector(
        `quantity-input[data-product-id="${pid}"] input[type="number"]`,
      );
      let qty = 1;
      if (qtyInput) {
        const v = parseInt(qtyInput.value, 10);
        if (Number.isFinite(v) && v > 0) qty = v;
      }
      contexts.push({
        productId: pid,
        basePrice,
        comparePrice,
        qty,
        priceEl,
        scope: root,
        productInfo: pi,
      });
    });
    return contexts;
  }

  // Retourne le contexte qui contient l'élément, ou null.
  function findContextForElement(el, contexts) {
    for (const ctx of contexts) {
      if (ctx.scope === document) continue; // on préfère un modal d'abord
      if (ctx.scope.contains(el)) return ctx;
    }
    return contexts.find((c) => c.scope === document) || null;
  }

  // Backward-compat : premier contexte (= fiche). Utilisé par lockedQueue.
  function getPageInfo() {
    return getPageContexts()[0] || null;
  }

  // ─── File de coches ordonnée ─────────────────────────────────────────────────

  const lockedQueue = []; // { priceEl, productId, basePrice, comparePrice, lockedPrice }

  function pruneStaleQueueEntries() {
    for (let i = lockedQueue.length - 1; i >= 0; i--) {
      if (
        !lockedQueue[i].priceEl ||
        !document.body.contains(lockedQueue[i].priceEl)
      ) {
        lockedQueue.splice(i, 1);
      }
    }
  }

  // ProductBundleDetails décoche sans event change → retirer les entrées orphelines
  // pour ne pas double-compter dans baseStateUnits ni figer un prix périmé.
  function syncLockedQueueWithDom() {
    pruneStaleQueueEntries();
    for (let i = lockedQueue.length - 1; i >= 0; i--) {
      const priceEl = lockedQueue[i].priceEl;
      const bundleItem = priceEl?.closest?.("[data-bundle-item]");
      const checkbox = bundleItem?.querySelector('input[name="bundles"]');
      if (!checkbox?.checked) lockedQueue.splice(i, 1);
    }
  }

  function queueIndexOf(priceEl) {
    return lockedQueue.findIndex((it) => it.priceEl === priceEl);
  }

  // Construit l'état de base du bloc additionnel : panier + fiche (sauf
  // suppressPage pack natif). Sert à la file de locks uniquement.
  function buildBasePageState(cartUnits, pageInfo) {
    const state = [...cartUnits];
    // [CUSTOM] Pack bundle natif (suppressPage) : la fiche ne compte pas dans
    // l'état du bloc additionnel (file de cochés). Le panier reste compté.
    const suppressFicheInBundle = window.__bundlePackCD?.suppressPage === true;
    if (pageInfo && !suppressFicheInBundle) {
      for (let i = 0; i < pageInfo.qty; i++) {
        state.push({
          productId: pageInfo.productId,
          basePrice: pageInfo.basePrice,
          comparePrice: pageInfo.comparePrice,
        });
      }
    }
    return state;
  }

  function buildStateUpToQueueIndex(idx, cartUnits, pageInfo) {
    const state = buildBasePageState(cartUnits, pageInfo);
    for (let j = 0; j < idx; j++) {
      const li = lockedQueue[j];
      state.push({
        productId: li.productId,
        basePrice: li.basePrice,
        comparePrice: li.comparePrice,
      });
    }
    return state;
  }

  // État avec tous les items cochés SAUF celui d'index idx.
  // Mode product_price : nécessaire pour que les options du <select> d'un
  // item coché voient le même palier que les autres items déjà cochés
  // (y compris ceux cochés APRÈS lui).
  function buildStateWithoutQueueIndex(idx, cartUnits, pageInfo) {
    const state = buildBasePageState(cartUnits, pageInfo);
    for (let j = 0; j < lockedQueue.length; j++) {
      if (j === idx) continue;
      const li = lockedQueue[j];
      state.push({
        productId: li.productId,
        basePrice: li.basePrice,
        comparePrice: li.comparePrice,
      });
    }
    return state;
  }

  // Recalcule lockedPrice d'un item à l'index idx.
  //
  //   • mode 'marginal' : coût marginal d'ajout séquentiel (panier + fiche +
  //     items cochés AVANT idx). Les items déjà cochés conservent leur prix
  //     figé quand on en coche un nouveau.
  //
  //   • mode 'product_price' : prix unitaire remisé dans l'état COMPLET
  //     (tous les items cochés). Cocher/décocher un item met à jour le
  //     palier — et donc le prix — de TOUS les items déjà cochés.
  function computeLockedPriceAtIndex(idx, cartUnits, pageInfo, cdRules) {
    const it = lockedQueue[idx];
    if (!it) return;

    if (priceMode === "product_price") {
      const fullState = buildBasePageState(cartUnits, pageInfo);
      const baseLen = fullState.length;
      for (const li of lockedQueue) {
        fullState.push({
          productId: li.productId,
          basePrice: li.basePrice,
          comparePrice: li.comparePrice,
        });
      }
      const u = computeUnitDiscounts(fullState, cdRules, true);
      const unit = u[baseLen + idx];
      if (!unit) {
        it.lockedPrice = Math.max(0, it.basePrice);
        return;
      }
      const raw = unit.isFixed
        ? Math.max(0, unit.fixedPrice)
        : unit.basePrice * (1 - unit.disc / 100);
      it.lockedPrice = Math.min(
        Math.max(0, Math.round(raw)),
        Math.max(0, it.basePrice),
      );
      return;
    }

    const stateBefore = buildStateUpToQueueIndex(idx, cartUnits, pageInfo);
    const stateAfter = [
      ...stateBefore,
      {
        productId: it.productId,
        basePrice: it.basePrice,
        comparePrice: it.comparePrice,
      },
    ];
    // Item coché du bloc additionnel → ajustement pack appliqué (applyPack).
    it.lockedPrice = cappedMarginal(
      stateBefore,
      stateAfter,
      it.basePrice,
      cdRules,
      true,
    );
  }

  // Recalcule à partir de fromIndex (décochage d'un item antérieur, changement
  // de variante, ou mise à jour panier). En mode product_price, fromIndex est
  // ignoré : tout le monde est recalculé (palier partagé).
  function recomputeLockedQueueSequential(
    fromIndex,
    cartUnits,
    pageInfo,
    cdRules,
  ) {
    pruneStaleQueueEntries();
    if (lockedQueue.length === 0) return;
    const cart = cartUnits || getCartUnits();
    const page = pageInfo !== undefined ? pageInfo : getPageInfo();
    const rules = cdRules || getCdRules();
    const start =
      priceMode === "product_price" ? 0 : Math.max(0, fromIndex);
    for (let i = start; i < lockedQueue.length; i++) {
      computeLockedPriceAtIndex(i, cart, page, rules);
    }
  }

  function enqueueLock(priceEl, productId, basePrice, comparePrice) {
    pruneStaleQueueEntries();
    if (queueIndexOf(priceEl) !== -1) return;
    lockedQueue.push({
      priceEl,
      productId,
      basePrice,
      comparePrice,
      lockedPrice: 0,
    });
    if (priceMode === "product_price") {
      // Nouveau palier possible → recalculer TOUS les items déjà cochés.
      recomputeLockedQueueSequential(0);
    } else {
      const idx = lockedQueue.length - 1;
      computeLockedPriceAtIndex(
        idx,
        getCartUnits(),
        getPageInfo(),
        getCdRules(),
      );
    }
  }

  function dequeueLock(priceEl) {
    const idx = queueIndexOf(priceEl);
    if (idx === -1) return false;
    lockedQueue.splice(idx, 1);
    recomputeLockedQueueSequential(0);
    return true;
  }

  function updateQueueEntry(priceEl, newBase, newCompare) {
    const idx = queueIndexOf(priceEl);
    if (idx === -1) return;
    lockedQueue[idx].basePrice = newBase;
    lockedQueue[idx].comparePrice = newCompare;
    // product_price : un changement de variante peut réordonner l'allocation
    // « Lowest priced first » → recalcul complet.
    recomputeLockedQueueSequential(
      priceMode === "product_price" ? 0 : idx,
    );
  }

  // Snapshot des items cochés pour les états de calcul. Pas d'isFixed : les
  // items cochés participent fluidement à l'allocation « Lowest priced first »,
  // ce qui permet à un nouvel item moins cher de leur prendre la remise.
  function getCheckedUnitsFromQueue() {
    pruneStaleQueueEntries();
    return lockedQueue.map((it) => ({
      productId: it.productId,
      basePrice: it.basePrice,
      comparePrice: it.comparePrice,
    }));
  }

  // ─── Core : allocation des remises ───────────────────────────────────────────
  //
  // Modèle d'allocation TB : « Lowest priced first » avec quota par palier.
  // Pour chaque règle TB :
  //   • on compte la quantité totale d'items éligibles (qty),
  //   • on détermine le palier le plus haut atteint (qty >= tier.qty),
  //   • on applique le pct du palier aux N items LES MOINS CHERS éligibles,
  //     où N = qty du palier atteint (capé au nombre d'items dispo).
  // → Ajouter un produit éligible peut donc faire perdre la remise à un produit
  //   plus cher qui l'avait, et la donner au nouvel arrivant.
  //
  // [CUSTOM] Règles progressive === true : modèle différent, chaque produit
  // reçoit la remise de SA position (tri par prix décroissant, position k =
  // tierPctFor(k)) au lieu d'un même % sur tous les éligibles. Voir la branche
  // rule.progressive ci-dessous.
  //
  // Les items isFixed (legacy) comptent dans le qty du palier mais sont
  // exclus de l'allocation (leur prix est figé).
  //
  // computeUnitDiscounts retourne le tableau u avec disc renseigné par item :
  // permet aux appelants de lire le prix post-remise individuel.
  // computeTotal somme simplement les contributions.
  // Ordre d'attribution des créneaux de remise : « Lowest priced first ».
  // Les unités isAdded (unité en cours d'évaluation, mode 'product_price')
  // passent APRÈS toutes les autres : elles ne prennent qu'un créneau resté
  // libre et ne volent jamais la remise d'une unité déjà présente. C'est ce
  // qui fait que la remise s'arrête au quota du palier au lieu de se propager
  // indéfiniment aux items non cochés.
  function compareForAllocation(a, b) {
    if (a.isAdded !== b.isAdded) return a.isAdded ? 1 : -1;
    return (
      a.basePrice * (1 - a.disc / 100) - b.basePrice * (1 - b.disc / 100)
    );
  }

  function computeUnitDiscounts(units, cdRules, applyPack) {
    const u = units.map((x) => ({
      productId: x.productId,
      basePrice: x.basePrice,
      comparePrice: x.comparePrice || 0,
      isFixed: !!x.isFixed,
      fixedPrice: x.fixedPrice,
      isAdded: !!x.isAdded,
      disc: 0,
    }));

    // [CUSTOM] Pack composé sélectionné (bundle-picker.js → window.__bundlePackCD
    // .addItems) : on AJOUTE au comptage (quantité requise des paliers / quotas)
    // les produits supplémentaires du pack — count-only, comme le extraReq de
    // conditional-discount.js (jamais d'unité éligible ajoutée).
    // UNIQUEMENT pour les prix du bloc de produits additionnels (applyPack=true) :
    // le prix de la fiche, des cartes et du cart-drawer n'est jamais ajusté.
    // Le cas « pack bundle Shopify natif » (retrait du produit de la fiche) est
    // géré à l'ASSEMBLAGE de l'état du bloc additionnel (baseStateUnits /
    // buildBasePageState) — pas ici — pour que le panier soit toujours compté
    // sans ambiguïté (aucune soustraction qui pourrait l'écraser).
    const packCD = applyPack ? window.__bundlePackCD || null : null;
    const packCountDelta = (idSet) => {
      if (!packCD || !Array.isArray(packCD.addItems)) return 0;
      let d = 0;
      for (const it of packCD.addItems) {
        if (idSet.has(it.productId)) d += it.quantity || 1;
      }
      return d;
    };

    for (const rule of tbRules) {
      // [CUSTOM] Remise progressive : chaque produit reçoit la remise de SA
      // position au lieu d'un même % sur tous les éligibles. Les éligibles sont
      // classés par prix décroissant → le plus cher occupe la position la plus
      // basse (remise la plus faible), le moins cher la position la plus haute
      // (remise la plus forte). La position k reçoit tierPctFor(k) ; au-delà du
      // dernier palier, le % du dernier palier est conservé (progression
      // naturellement illimitée, lastUnlimited sans effet ici).
      if (rule.progressive) {
        const eligible = u.filter(
          (it) => !it.isFixed && rule.productIdSet.has(it.productId),
        );
        // Prix effectif décroissant ; à prix égal, une unité en cours d'ajout
        // (isAdded) passe en dernier → prend la position restante la plus
        // remisée (cohérent avec l'aperçu « prix une fois ajouté »).
        eligible.sort((a, b) => {
          const pa = a.basePrice * (1 - a.disc / 100);
          const pb = b.basePrice * (1 - b.disc / 100);
          if (pb !== pa) return pb - pa;
          if (a.isAdded !== b.isAdded) return a.isAdded ? 1 : -1;
          return 0;
        });
        // packCountDelta : produits supplémentaires du pack (count-only, prix
        // inconnu) → traités comme occupant les positions les plus chères (les
        // moins remisées), décalant les éligibles vers des positions plus
        // remisées.
        const posOffset = Math.max(0, packCountDelta(rule.productIdSet));
        eligible.forEach((it, i) => {
          const pct = tierPctFor(i + 1 + posOffset, rule.tiers);
          if (pct > it.disc) it.disc = pct;
        });
        allocateGifts(u, rule, eligible.length + posOffset);
        continue;
      }

      let qty = 0;
      for (const it of u) if (rule.productIdSet.has(it.productId)) qty++;
      qty = Math.max(0, qty + packCountDelta(rule.productIdSet));
      // [CUSTOM] Cadeaux des paliers franchis (100 % de remise), indépendants du
      // % du palier atteint : ils s'appliquent même si le palier n'a pas de
      // réduction. Alloués AVANT le % pour qu'une unité déjà offerte ne consomme
      // pas un créneau de remise de palier.
      allocateGifts(u, rule, qty);
      let achievedTier = null;
      for (const t of rule.tiers) {
        if (qty >= t.qty) achievedTier = t;
        else break;
      }
      if (!achievedTier) continue;
      const pct = achievedTier.pct;
      if (pct <= 0) continue;
      const eligible = u.filter(
        (it) =>
          !it.isFixed && rule.productIdSet.has(it.productId) && pct > it.disc,
      );
      eligible.sort(compareForAllocation);
      // Dernier palier atteint + « dernier palier illimité » → aucune limite de
      // quota (toutes les unités éligibles remisées) ; sinon plafond = quota du
      // palier atteint (les unités LES MOINS CHÈRES, cf. compareForAllocation).
      const isLastTier = achievedTier === rule.tiers[rule.tiers.length - 1];
      const slots =
        rule.lastUnlimited && isLastTier
          ? eligible.length
          : Math.min(achievedTier.qty, eligible.length);
      for (let i = 0; i < slots; i++) {
        eligible[i].disc = pct;
      }
    }

    for (const rule of cdRules) {
      let req = 0;
      for (const it of u) if (rule.requiredProductIds.has(it.productId)) req++;
      req = Math.max(0, req + packCountDelta(rule.requiredProductIds));
      const quota = Math.floor(req / rule.requiredQty) * rule.discountedQty;
      if (quota <= 0) continue;
      const eligible = u.filter(
        (it) =>
          !it.isFixed &&
          rule.discountedProductIds.has(it.productId) &&
          rule.discountPct > it.disc,
      );
      eligible.sort(compareForAllocation);
      const slots = Math.min(quota, eligible.length);
      for (let i = 0; i < slots; i++) {
        eligible[i].disc = rule.discountPct;
      }
    }

    return u;
  }

  function computeTotal(units, cdRules, applyPack) {
    const u = computeUnitDiscounts(units, cdRules, applyPack);
    let total = 0;
    for (const it of u) {
      if (it.isFixed) total += Math.max(0, it.fixedPrice);
      else total += it.basePrice * (1 - it.disc / 100);
    }
    return total;
  }

  // [CUSTOM] Économie générée sur les AUTRES unités (panier) par l'ajout d'un
  // produit : baisse du sous-total des unités déjà présentes (hors la nouvelle).
  //   savings = total(base) − [total(base+produit) − prix remisé du produit].
  // Sert au badge « économie panier » quand le produit ajouté n'a pas de remise
  // propre mais fait progresser les paliers des autres.
  function cartSavingsFromAdding(baseUnits, productUnit, cdRules) {
    const withUnits = [...baseUnits, productUnit];
    const u = computeUnitDiscounts(withUnits, cdRules, false);
    const prod = u[u.length - 1];
    if (!prod) return 0;
    const productLine = prod.isFixed
      ? Math.max(0, prod.fixedPrice)
      : prod.basePrice * (1 - prod.disc / 100);
    let totalWith = 0;
    for (const it of u) {
      totalWith += it.isFixed
        ? Math.max(0, it.fixedPrice)
        : it.basePrice * (1 - it.disc / 100);
    }
    const totalWithout = computeTotal(baseUnits, cdRules, false);
    return Math.max(0, Math.round(totalWithout - (totalWith - productLine)));
  }

  // Prix des unités AJOUTÉES entre deux états, selon le mode d'affichage.
  // Par convention tous les appelants construisent stateWithItem en ajoutant
  // les unités concernées EN FIN de stateWithoutItem.
  //
  //   • mode 'marginal' (défaut) : coût réellement ajouté au panier,
  //     total(stateWithItem) − total(stateWithoutItem). Inclut donc les
  //     économies générées sur les autres produits du panier.
  //
  //   • mode 'product_price' : prix des seules unités ajoutées après remise,
  //     lu directement sur elles dans l'état post-ajout. Les remises que leur
  //     ajout déclenche sur les AUTRES produits ne sont pas déduites.
  //     Ex. produit à 20 € qui atteint le palier -10 % → 18 €, même si le
  //     panier n'augmente réellement que de 14,50 €.
  function addedCost(stateWithoutItem, stateWithItem, cdRules, applyPack) {
    if (priceMode !== "product_price") {
      return computeTotal(stateWithItem, cdRules, applyPack) -
        computeTotal(stateWithoutItem, cdRules, applyPack);
    }
    // Les unités ajoutées sont marquées isAdded : elles ne peuvent prendre
    // qu'un créneau de remise resté libre (cf. compareForAllocation).
    const marked = stateWithItem.map((it, i) =>
      i < stateWithoutItem.length ? it : { ...it, isAdded: true },
    );
    const u = computeUnitDiscounts(marked, cdRules, applyPack);
    let sum = 0;
    for (let i = stateWithoutItem.length; i < u.length; i++) {
      const it = u[i];
      if (!it) continue;
      sum += it.isFixed
        ? Math.max(0, it.fixedPrice)
        : it.basePrice * (1 - it.disc / 100);
    }
    return sum;
  }

  // Prix affiché d'un item ajouté à un état (voir addedCost pour le calcul).
  //
  // Plafonné au prix de base (jamais d'affichage au-dessus du prix natif) et
  // planché à 0. Couvre le cas « Lowest priced first » : ajouter un produit
  // moins cher qui prend la remise à un plus cher peut coûter PLUS que le prix
  // de base du nouveau produit (parce qu'on perd la remise sur l'ancien). Dans
  // ce cas on s'arrête au prix de base, sans balise de réduction.
  function cappedMarginal(
    stateWithoutItem,
    stateWithItem,
    basePrice,
    cdRules,
    applyPack,
  ) {
    const cost = Math.max(
      0,
      Math.round(addedCost(stateWithoutItem, stateWithItem, cdRules, applyPack)),
    );
    return Math.min(cost, Math.max(0, basePrice));
  }

  // ─── Rendu DOM ───────────────────────────────────────────────────────────────

  function setTbActive(el, active) {
    if (active) el.setAttribute("data-tb-active", "");
    else el.removeAttribute("data-tb-active");
  }

  // Zones HORIZONTALES (cart drawer recents/complementary, comparison table,
  // etc.) : pas de container .badges, donc le span « Jusqu'à -X% » inline
  // est conservé en fromMode comme indicateur. Pas de badge inline (juste
  // prix + prix barré).
  function isInHorizontalZone(el) {
    return !!el.closest(".horizontal-product, .comparison-table");
  }

  function applyOutsidePrice(
    el,
    displayedPrice,
    basePrice,
    comparePrice,
    upToPctOverride,
  ) {
    const tbAffects = displayedPrice < basePrice;
    const fromMode = isFromMode(el);
    const horiz = isInHorizontalZone(el);

    if (fromMode) {
      // Mode « Dès … » [CUSTOM : + prix barré, cf. fromMarkup].
      // - Zones horizontales : on inclut le span .price__discount-range inline
      //   (pas de badge à part).
      // - Cards / fiche / modal : la badge externe gère le « Jusqu'à ». Pas
      //   de discount-range inline (CSS le masque en .product-card de toute
      //   façon).
      let upToPct = 0;
      if (horiz) {
        if (typeof upToPctOverride === "number" && upToPctOverride > 0) {
          upToPct = upToPctOverride;
        } else {
          const baseUpTo = parseFloat(el.dataset.tbBaseUpToPct) || 0;
          const tbPct =
            basePrice > 0
              ? ((basePrice - displayedPrice) * 100) / basePrice
              : 0;
          upToPct = Math.max(0, Math.round(Math.max(baseUpTo, tbPct)));
        }
      }
      // [CUSTOM] Prix barré en mode « Dès » : compare_at de la variante au
      // prix le plus bas (prix non réduit). Sans compare_at sur cette
      // variante, repli sur price_min si une remise TB s'applique.
      const fromCap = resolveFromCap(
        displayedPrice,
        basePrice,
        comparePrice,
        el,
      );
      el.classList.toggle("price--on-sale", fromCap > displayedPrice);
      el.innerHTML = fromMarkup(displayedPrice, el, horiz, upToPct, fromCap);
      setTbActive(el, tbAffects);
      return;
    }

    if (tbAffects) {
      const cap = comparePrice > basePrice ? comparePrice : basePrice;
      el.classList.add("price--on-sale");
      // Plus de badge inline en zones horizontales — juste prix + prix barré.
      el.innerHTML = discountedMarkup(Math.max(0, displayedPrice), cap, false);
      setTbActive(el, true);
    } else if (el.hasAttribute("data-tb-active")) {
      el.classList.toggle("price--on-sale", comparePrice > basePrice);
      el.innerHTML = regularMarkup(basePrice, comparePrice, false);
      setTbActive(el, false);
    }
  }

  function applyBundlePrice(el, displayedPrice, basePrice, comparePrice, opts) {
    const bundleItem = el.closest("[data-bundle-item]");
    const checkbox = bundleItem?.querySelector('input[name="bundles"]');
    const displayInt = Math.max(0, Math.round(displayedPrice));
    // forceVariantMode = true → ignore fromMode, render variant-specific
    // (badge « Epargnez X% » inline, prix avec strikethrough).
    // [CUSTOM] forceFromMode = true → force le rendu « Dès … » (placeholder
    // « sélectionner une variante » de custom_preselect-variant).
    const fromMode = opts?.forceVariantMode
      ? false
      : opts?.forceFromMode
        ? true
        : isFromMode(el);
    const upToPctOverride = opts?.upToPct;

    if (fromMode) {
      // Bundle item en mode « Dès » : on garde le span .price__discount-range
      // inline car il n'y a pas de container .badges à part dans un bundle item.
      let upToPct;
      if (typeof upToPctOverride === "number" && upToPctOverride > 0) {
        upToPct = upToPctOverride;
      } else {
        const baseUpTo = parseFloat(el.dataset.tbBaseUpToPct) || 0;
        const tbPct =
          basePrice > 0 ? ((basePrice - displayInt) * 100) / basePrice : 0;
        upToPct = Math.max(0, Math.round(Math.max(baseUpTo, tbPct)));
      }
      const fromCap = resolveFromCap(displayInt, basePrice, comparePrice, el);
      el.classList.toggle("price--on-sale", fromCap > displayInt);
      el.innerHTML = fromMarkup(displayInt, el, true, upToPct, fromCap);
      // [CUSTOM] Mémorise le upToPct réellement affiché → figé au cochage placeholder
      el.dataset.tbLastUpToPct = String(upToPct);
    } else if (displayInt < basePrice) {
      // Bundle item avec remise : badge inline (pas de .badges container).
      const cap = comparePrice > basePrice ? comparePrice : basePrice;
      const pid = parseInt(el.dataset.productId, 10);
      el.classList.add("price--on-sale");
      el.innerHTML = discountedMarkup(displayInt, cap, true, pid, basePrice);
      delete el.dataset.tbLastUpToPct;
    } else {
      // Pas de remise TB : on rend le markup normal en gardant le badge inline
      // si une promo Shopify (compare_at) est présente.
      const pid = parseInt(el.dataset.productId, 10);
      el.classList.toggle("price--on-sale", comparePrice > basePrice);
      el.innerHTML = regularMarkup(basePrice, comparePrice, true, pid);
      delete el.dataset.tbLastUpToPct;
    }
    if (checkbox) checkbox.setAttribute("data-price", displayInt);
    el.dataset.tbDisplayPrice = String(displayInt);
  }

  // Tiered bundle calcule le marginal ; la CD décide si l'item bundle doit
  // AFFICHER une promo (slotsAfterCart, cheapest-first…). Prend le meilleur prix.
  function mergeCdBundleDisplayPrice(el, basePrice, tbNet) {
    const display = Math.max(0, Math.round(tbNet));
    const cdApi = window.__cdBundlePricing;
    if (!cdApi?.computeItemState || !(basePrice > 0)) return display;
    const cdState = cdApi.computeItemState(el);
    if (!cdState?.active || !(cdState.discountPct > 0)) return display;
    const cdPrice = Math.round((basePrice * (100 - cdState.discountPct)) / 100);
    return Math.min(display, cdPrice);
  }

  // Le sticky reprend exactement le rendu du bouton ATC (innerHTML + data-price +
  // _originalHTML) pour garantir le même montant affiché et le même calcul.
  function getProductInfoForForm(form) {
    if (!form) return null;
    return (
      form.closest("product-info[data-product-id]") ||
      form
        .closest(".shopify-section, .featured-product, .quick-view, .x-modal")
        ?.querySelector("product-info[data-product-id]")
    );
  }

  function findAtcBuyPrice(form) {
    const productInfo = getProductInfoForForm(form);
    if (productInfo?.dataset.sectionId && productInfo?.dataset.productId) {
      const byId = document.querySelector(
        `#BuyButtonPrice-${productInfo.dataset.sectionId}-${productInfo.dataset.productId} product-buy-price[data-price]`,
      );
      if (byId) return byId;
    }
    if (!form?.id) return null;
    const formSel = CSS.escape(form.id);
    return (
      form.querySelector(`product-buy-price[form="${formSel}"][data-price]`) ||
      document.querySelector(
        `product-buy-price[form="${formSel}"][data-price]:not([data-sticky-atc-price])`,
      )
    );
  }

  function findStickyBuyPrice(form) {
    const productInfo = getProductInfoForForm(form);
    if (productInfo?.dataset.sectionId && productInfo?.dataset.productId) {
      const { sectionId, productId } = productInfo.dataset;
      const byId =
        document.querySelector(
          `#StickyBuyButtonPrice-${sectionId}-${productId} product-buy-price[data-price]`,
        ) ||
        document.querySelector(
          `#StickyPrice-${sectionId}-${productId} product-buy-price[data-price]`,
        );
      if (byId) return byId;
    }
    if (!form?.id) return null;
    const formSel = CSS.escape(form.id);
    return document.querySelector(
      `product-sticky-form[form="${formSel}"] product-buy-price[data-price]`,
    );
  }

  function getMainProductPriceEl(form) {
    const productInfo = getProductInfoForForm(form);
    if (!productInfo?.dataset.sectionId || !productInfo?.dataset.productId)
      return null;
    return document.querySelector(
      `#Price-${productInfo.dataset.sectionId}-${productInfo.dataset.productId} .price[data-base-price]`,
    );
  }

  function stickySaleMarkup(currentCents, compareCents) {
    return (
      `<span class="sr-only">${tbConfig.i18n.salePriceLabel}</span>` +
      `<span class="sticky-buy-price__current whitespace-nowrap">${formatMoney(currentCents)}</span>` +
      `<span class="sr-only">${tbConfig.i18n.regularPriceLabel}</span>` +
      `<span class="sticky-buy-price__compare inline-flex items-center h-auto relative whitespace-nowrap">${formatMoney(compareCents)}</span>`
    );
  }

  function renderStickyUnitPrice(
    stickyPrice,
    displayedInt,
    basePrice,
    comparePrice,
  ) {
    if (!Number.isFinite(displayedInt) || displayedInt < 0) return false;

    if (displayedInt < basePrice) {
      const cap = comparePrice > basePrice ? comparePrice : basePrice;
      stickyPrice.classList.add("price--on-sale");
      stickyPrice.innerHTML = stickySaleMarkup(displayedInt, cap);
      return true;
    }

    if (comparePrice > displayedInt && comparePrice > 0) {
      stickyPrice.classList.add("price--on-sale");
      stickyPrice.innerHTML = stickySaleMarkup(displayedInt, comparePrice);
      return true;
    }

    stickyPrice.classList.remove("price--on-sale");
    return false;
  }

  // [CUSTOM] « Dès … » sur le sticky tant qu'une variante n'est pas sélectionnée :
  //  • fiche produit à prix variables : recopie du « Dès €… » du prix principal ;
  //  • produit additionnel coché resté sur le placeholder « sélectionner une
  //    variante » avec prix variables : « Dès (total minimum) ».
  // Retourne le HTML à afficher, ou null (comportement natif).
  function stickyPendingFromHtml(
    form,
    section,
    stickyPrice,
    atcDataPrice,
    checkedCount,
  ) {
    if (!section) return null;

    let bundlePendingVaries = false;
    const checkedBundles = section.querySelectorAll(
      'input[name="bundles"]:checked',
    );
    checkedBundles.forEach((cb) => {
      const item = cb.closest("[data-bundle-item]");
      const sel = item?.querySelector("[data-bundle-variant-select]");
      const selOpt = sel?.selectedOptions?.[0];
      if (!selOpt?.hasAttribute?.("data-bundle-variant-placeholder")) return;
      const priceEl = item?.querySelector("[data-bundle-price]");
      const min = parseInt(priceEl?.dataset.priceMin, 10);
      const max = parseInt(priceEl?.dataset.priceMax, 10);
      if (Number.isFinite(min) && Number.isFinite(max) && max > min) {
        bundlePendingVaries = true;
      }
    });

    const mainFromEl = getMainProductPriceEl(form)?.querySelector(
      ".price__regular--from",
    );

    if (!bundlePendingVaries && !mainFromEl) return null;

    // Texte brut (pas de spans .price__regular : le sticky a son propre style,
    // même logique que le rendu « Dès » du sticky dans custom_preselect-variant)
    if (checkedCount === 0 && mainFromEl) {
      // Fiche seule : recopier exactement le « Dès €… » du prix principal
      return mainFromEl.textContent.trim();
    }

    // Total minimum = prix affiché fiche (data-price ATC) + somme des data-price
    // des items cochés (les items en attente portent leur prix minimum)
    let total = parseInt(atcDataPrice, 10) || 0;
    checkedBundles.forEach((cb) => {
      total += parseInt(cb.getAttribute("data-price"), 10) || 0;
    });
    return fromTemplate(stickyPrice).replace("__amount__", formatMoney(total));
  }

  // [CUSTOM] Pack (bundle-picker) sélectionné : le prix ATC est géré par
  // bundle-picker.js — TB ne doit ni le recalculer ni reconstruire le sticky
  // depuis le prix unitaire de la fiche.
  function isBundlePackActive() {
    return window.__bundlePackCD != null;
  }

  function syncStickyBuyPriceFromAtc(form) {
    const atcPrice = findAtcBuyPrice(form);
    const stickyPrice = findStickyBuyPrice(form);
    if (!atcPrice || !stickyPrice || atcPrice === stickyPrice) return;

    const atcDataPrice = atcPrice.getAttribute("data-price");
    if (atcDataPrice != null)
      stickyPrice.setAttribute("data-price", atcDataPrice);

    const section = form.closest(
      ".shopify-section, .featured-product, .quick-view, .x-modal",
    );
    const checkedCount = section
      ? section.querySelectorAll('input[name="bundles"]:checked').length
      : 0;

    // [CUSTOM] Pack actif → miroir direct du bouton ATC (prix pack), jamais le
    // rendu unitaire fiche / « Dès … » qui écraserait syncStickyForPack.
    if (isBundlePackActive()) {
      stickyPrice.classList.remove("price--on-sale");
      stickyPrice.innerHTML = atcPrice.innerHTML;
      if ("_originalHTML" in stickyPrice) {
        stickyPrice._originalHTML = stickyPrice.innerHTML;
      }
      const packStickyRoot = stickyPrice.closest('[id^="StickyPrice-"]');
      const packStickyBadge = packStickyRoot?.querySelector(
        "[data-sticky-bundle-badge]",
      );
      if (packStickyBadge) {
        if (checkedCount > 0) {
          packStickyBadge.textContent = `+ ${checkedCount} article${checkedCount > 1 ? "s" : ""}`;
          packStickyBadge.removeAttribute("hidden");
        } else {
          packStickyBadge.setAttribute("hidden", "");
        }
      }
      return;
    }

    // [CUSTOM] Sélection de variante en attente → « Dès … » sur le sticky
    const pendingFromHtml = stickyPendingFromHtml(
      form,
      section,
      stickyPrice,
      atcDataPrice,
      checkedCount,
    );
    if (pendingFromHtml !== null) {
      stickyPrice.classList.remove("price--on-sale");
      stickyPrice.innerHTML = pendingFromHtml;
      if ("_originalHTML" in stickyPrice) {
        stickyPrice._originalHTML = stickyPrice.innerHTML;
      }
      const pendingStickyRoot = stickyPrice.closest('[id^="StickyPrice-"]');
      const pendingStickyBadge = pendingStickyRoot?.querySelector(
        "[data-sticky-bundle-badge]",
      );
      if (pendingStickyBadge) {
        if (checkedCount > 0) {
          pendingStickyBadge.textContent = `+ ${checkedCount} article${checkedCount > 1 ? "s" : ""}`;
          pendingStickyBadge.removeAttribute("hidden");
        } else {
          pendingStickyBadge.setAttribute("hidden", "");
        }
      }
      return;
    }

    if (checkedCount > 0) {
      stickyPrice.classList.remove("price--on-sale");
      stickyPrice.innerHTML = atcPrice.innerHTML;
    } else {
      const displayedInt = parseInt(atcDataPrice, 10);
      const mainPriceEl = getMainProductPriceEl(form);
      const basePrice = mainPriceEl
        ? parseInt(mainPriceEl.dataset.basePrice, 10) || displayedInt
        : displayedInt;
      const comparePrice = mainPriceEl
        ? parseInt(mainPriceEl.dataset.baseCompareAtPrice, 10) || 0
        : 0;

      if (
        !renderStickyUnitPrice(
          stickyPrice,
          displayedInt,
          basePrice,
          comparePrice,
        )
      ) {
        stickyPrice.innerHTML = atcPrice.innerHTML;
      }
    }

    if ("_originalHTML" in stickyPrice) {
      stickyPrice._originalHTML = stickyPrice.innerHTML;
    }

    const stickyRoot = stickyPrice.closest('[id^="StickyPrice-"]');
    const stickyBadge = stickyRoot?.querySelector("[data-sticky-bundle-badge]");
    if (!stickyBadge) return;
    if (checkedCount > 0) {
      stickyBadge.textContent = `+ ${checkedCount} article${checkedCount > 1 ? "s" : ""}`;
      stickyBadge.removeAttribute("hidden");
    } else {
      stickyBadge.setAttribute("hidden", "");
    }
  }

  function syncAllStickyBuyPricesFromAtc() {
    document
      .querySelectorAll('[data-type="add-to-cart-form"]')
      .forEach((form) => {
        syncStickyBuyPriceFromAtc(form);
      });
  }
  window.__syncStickyBuyPricesFromAtc = syncAllStickyBuyPricesFromAtc;
  window.__syncStickyBuyPriceFromAtc = syncStickyBuyPriceFromAtc;

  function applyProductBuyPrice(el, displayedPrice, basePrice, comparePrice) {
    const displayInt = Math.max(0, Math.round(displayedPrice));
    el.setAttribute("data-price", displayInt);

    let nested = el.querySelector(".price[data-base-price]");
    const hadNested = el.dataset.tbHasNested === "true";

    if (nested) {
      el.dataset.tbHasNested = "true";
      applyOutsidePrice(nested, displayInt, basePrice, comparePrice);
      if ("_originalHTML" in el) el._originalHTML = el.innerHTML;
      return;
    }

    if (hadNested) {
      // Sticky/StickyPrice : le nested .price a été wipé par la branche
      // « items > 0 » de product-buy-price.onBundleChanged (innerHTML = plain
      // money string). On régénère un .price[data-base-price] et on lui
      // applique le markup TB → le sticky retrouve son rendu stylé et le
      // _originalHTML est ré-aligné, ce qui élimine le flicker
      // « MAJ → minuscule → MAJ ».
      const productId = getProductIdForBuyPrice(el);
      const fresh = document.createElement("div");
      fresh.className = "price";
      fresh.setAttribute("data-base-price", basePrice);
      fresh.setAttribute("data-base-compare-at-price", comparePrice || 0);
      if (productId != null) fresh.setAttribute("data-product-id", productId);
      el.innerHTML = "";
      el.appendChild(fresh);
      applyOutsidePrice(fresh, displayInt, basePrice, comparePrice);
      if ("_originalHTML" in el) el._originalHTML = el.innerHTML;
      return;
    }

    // Cas BuyButtonPrice (ATC button) : pas de nested initialement. On rend
    // en TEXTE BRUT (sans spans .price__regular / .price__sale), car ces
    // classes ont un font-size propre (var(--fluid-sm-to-base)) qui rapetisse
    // le texte du bouton et change visuellement la casse héritée (text-transform
    // du .button). Le rendu natif Liquid de BuyButtonPrice est juste un
    // money string ; on s'aligne dessus pour conserver le style du bouton.
    el.innerHTML = formatMoney(displayInt);
    if ("_originalHTML" in el) el._originalHTML = el.innerHTML;
  }

  function getProductIdForBuyPrice(el) {
    const nested = el.querySelector(".price[data-product-id][data-base-price]");
    if (nested) return parseInt(nested.dataset.productId, 10);
    const formId = el.getAttribute("form");
    if (!formId) return null;
    const form = document.getElementById(formId);
    const scope = form?.closest(".shopify-section, .quick-view, .x-modal");
    const productInfo = scope?.querySelector("product-info[data-product-id]");
    return productInfo ? parseInt(productInfo.dataset.productId, 10) : null;
  }

  // ─── Sélecteur de variantes d'un item du bloc additionnel ───────────────────

  function updateBundleVariantSelectForItem(
    bundleItem,
    stateForOption,
    cdRules,
  ) {
    const select = bundleItem.querySelector("[data-bundle-variant-select]");
    if (!select) return;
    const pid = parseInt(bundleItem.dataset.productId, 10);
    if (!Number.isFinite(pid)) return;

    Array.from(select.options).forEach((opt) => {
      if (opt.disabled) return;
      const variantPrice = parseInt(opt.dataset.price, 10);
      const variantCompare = parseInt(opt.dataset.comparePrice, 10) || 0;
      if (!Number.isFinite(variantPrice) || variantPrice <= 0) return;
      // Prix affiché de chaque option = coût MARGINAL capé au prix de la
      // variante. Cohérent avec le prix du bundle item lui-même.
      const withOpt = [
        ...stateForOption,
        {
          productId: pid,
          basePrice: variantPrice,
          comparePrice: variantCompare,
        },
      ];
      const net = cappedMarginal(
        stateForOption,
        withOpt,
        variantPrice,
        cdRules,
        true,
      );

      if (!opt.dataset.tbOriginalText) opt.dataset.tbOriginalText = opt.text;
      const originalText = opt.dataset.tbOriginalText;
      const originalMoney = formatMoney(variantPrice);
      const newMoney = formatMoney(net);
      if (net !== variantPrice && originalText.indexOf(originalMoney) !== -1) {
        opt.text = originalText.replace(originalMoney, newMoney);
      } else {
        opt.text = originalText;
      }
    });
  }

  // ─── Boucle d'évaluation ─────────────────────────────────────────────────────

  let evaluating = false;
  function evaluateAndApply() {
    if (evaluating) return;
    evaluating = true;
    try {
      _evaluate();
    } finally {
      evaluating = false;
    }
  }

  // État panier + qty fiche + items cochés pour le calcul des options
  // du <select> d'un item coché :
  //   • 'marginal'      : items cochés AVANT idx (ordre séquentiel figé) ;
  //   • 'product_price' : TOUS les autres items cochés (palier partagé).
  function buildStateExcludingQueueIndex(idx, cartUnits, pageInfo) {
    if (priceMode === "product_price") {
      return buildStateWithoutQueueIndex(idx, cartUnits, pageInfo);
    }
    return buildStateUpToQueueIndex(idx, cartUnits, pageInfo);
  }

  function _evaluate() {
    syncLockedQueueWithDom();
    const cdRules = getCdRules();
    const cartUnits = getCartUnits();
    // [CUSTOM] Unités du pack collection bundle → aperçu « panier + pack » sur
    // la grille et le modal « choisir les options » (voir getBundlePackUnits).
    const bundlePackUnits = getBundlePackUnits();
    const contexts = getPageContexts();
    const ficheCtx =
      contexts.find((c) => c.scope === document) || contexts[0] || null;

    const checkedUnits = getCheckedUnitsFromQueue();

    // Mode product_price : les prix des items cochés suivent le palier courant
    // (et le pack CD éventuel). On recalcule systématiquement avant le rendu —
    // contrairement au mode marginal où le prix reste figé à la coche.
    if (priceMode === "product_price" && lockedQueue.length > 0) {
      recomputeLockedQueueSequential(0, cartUnits, ficheCtx, cdRules);
    }

    // baseStateUnits : panier + qty de la FICHE (pour la file de locks et les
    // items du bloc additionnel rattachés à la fiche).
    // [CUSTOM] Pack bundle Shopify natif sélectionné (window.__bundlePackCD
    // .suppressPage) : le produit de la fiche ne compte PAS dans la quantité
    // requise du bloc additionnel (il est éclaté dans le pack, ligne séparée) →
    // on ne l'ajoute simplement pas à l'état de base. Le PANIER (cartUnits) reste
    // toujours compté. N'affecte QUE le bloc additionnel : le prix de la fiche
    // utilise ctxBaseUnits (calculé séparément), jamais baseStateUnits.
    const suppressFicheInBundle = window.__bundlePackCD?.suppressPage === true;
    const baseStateUnits = [...cartUnits];
    if (ficheCtx && !suppressFicheInBundle) {
      for (let i = 0; i < ficheCtx.qty; i++) {
        baseStateUnits.push({
          productId: ficheCtx.productId,
          basePrice: ficheCtx.basePrice,
          comparePrice: ficheCtx.comparePrice,
        });
      }
    }
    for (const u of checkedUnits) baseStateUnits.push(u);

    // Ensemble des priceEl déjà traités par les contextes (à exclure de la
    // boucle « cartes »).
    const handledPriceEls = new Set();

    // Helper : calcule + applique le rendu pour TOUS les .price d'un contexte
    // (titre + tout .price interne d'un product-buy-price du même produit dans
    // le même scope). Évite la désync entre prix du titre et prix du sticky.
    function processContextPriceEl(ctx) {
      if (ctx.productInfo && !shouldShowVariantPreview(ctx.productInfo)) {
        const selectedVariant = detectVariantInScope(ctx.productInfo);
        if (selectedVariant) {
          const variantPrice = parseInt(selectedVariant.price, 10);
          const variantCompare =
            parseInt(selectedVariant.compare_at_price, 10) || 0;
          if (Number.isFinite(variantPrice) && variantPrice > 0) {
            ctx.basePrice = variantPrice;
            ctx.comparePrice = variantCompare;
            ctx.priceEl.dataset.basePrice = String(variantPrice);
            ctx.priceEl.dataset.baseCompareAtPrice = String(variantCompare);
          }
        }
      }

      const fromMode = isFromMode(ctx.priceEl);
      const variants = getProductVariants(ctx.productId, ctx.priceEl);
      const previewPending =
        ctx.productInfo && shouldShowVariantPreview(ctx.productInfo);
      const samePricePreview =
        previewPending && hasSamePriceVariantSale(variants)
          ? getSamePriceSalePreviewData(variants)
          : null;

      // Prix affiché du produit principal, par unité, capé au prix de base.
      //
      // État de référence selon le mode :
      //   • 'marginal'      : panier UNIQUEMENT (pas les items cochés du bloc
      //     additionnel) — la fiche reste stable quand l'utilisateur
      //     coche/décoche, et une réduction déclenchée par un item coché ne
      //     vient pas réduire le prix de la fiche.
      //   • 'product_price' : panier + items cochés — cocher un item qui fait
      //     atteindre un palier couvrant AUSSI le produit principal met à jour
      //     son prix, son badge, et par ricochet l'ATC et le sticky (tous les
      //     .price du contexte sont rendus ici).
      // [CUSTOM] Ajoute les unités du pack (sauf quick view « œil ») → le modal
      // « choisir les options » aperçoit le prix/réduction panier + pack.
      const ctxPackUnits = contextIncludesPack(ctx) ? bundlePackUnits : [];
      const ctxBaseUnits =
        priceMode === "product_price"
          ? [...cartUnits, ...checkedUnits, ...ctxPackUnits]
          : [...cartUnits, ...ctxPackUnits];
      const pageBase = samePricePreview
        ? samePricePreview.minPrice
        : ctx.basePrice;
      const pageCompare = samePricePreview
        ? samePricePreview.maxCompareAt
        : ctx.comparePrice;
      const withCtx = [
        ...ctxBaseUnits,
        ...Array.from({ length: ctx.qty }, () => ({
          productId: ctx.productId,
          basePrice: pageBase,
          comparePrice: pageCompare,
        })),
      ];
      const aggregateMarginal = Math.max(
        0,
        Math.round(addedCost(ctxBaseUnits, withCtx, cdRules)),
      );
      const perUnitMarginal =
        ctx.qty > 0 ? Math.round(aggregateMarginal / ctx.qty) : 0;
      const displayedUnit = Math.min(perUnitMarginal, Math.max(0, pageBase));

      let displayBase = pageBase;
      let compareForDisplay = pageCompare;
      let displayed = displayedUnit;

      // Mode « Dès » : marginal capé d'UNE copie à priceMin ajoutée au panier
      // seul. Aperçu de la variante la moins chère, stable lui aussi.
      if (fromMode && ctx.priceEl.dataset.tbPriceMin) {
        const priceMin = parseInt(ctx.priceEl.dataset.tbPriceMin, 10);
        if (Number.isFinite(priceMin) && priceMin > 0) {
          displayBase = priceMin;
          const stateMin = [
            ...ctxBaseUnits,
            { productId: ctx.productId, basePrice: priceMin, comparePrice: 0 },
          ];
          displayed = cappedMarginal(ctxBaseUnits, stateMin, priceMin, cdRules);
        }
      }

      // Calcule upToPct AVANT de rendre les .price : on cherche le MAX réel
      // parmi toutes les variantes (en réappliquant TB+CD à chacune), pour
      // que « Jusqu'à -X% » reflète l'économie maximale observable et pas
      // seulement le tbPct de priceMin. Même état que displayed (ctxBaseUnits)
      // pour rester cohérent avec le prix affiché.
      const baseUpTo = parseFloat(ctx.priceEl.dataset.tbBaseUpToPct) || 0;
      const tbPct =
        displayBase > 0 ? ((displayBase - displayed) * 100) / displayBase : 0;
      let upToPct = Math.max(0, Math.round(Math.max(baseUpTo, tbPct)));
      if (fromMode) {
        const variantsMax = computeMaxDiscountAcrossVariants(
          ctx.productId,
          ctxBaseUnits,
          cdRules,
          ctx.priceEl,
        );
        if (variantsMax > upToPct) upToPct = variantsMax;
      }

      // Applique le rendu à TOUS les .price du produit dans le scope du
      // contexte (titre principal). Le sticky add to cart reprend le prix du
      // bouton ATC via syncStickyBuyPriceFromAtc (évite le prix « Dès » / nested).
      const root = ctx.scope === document ? document : ctx.scope;
      const ownPriceEls = root.querySelectorAll(
        `.price[data-product-id="${ctx.productId}"][data-base-price]:not([data-bundle-price]):not([data-bundle-pack-expand])`,
      );
      ownPriceEls.forEach((pEl) => {
        if (pEl.closest("product-sticky-form")) return;
        applyOutsidePrice(
          pEl,
          displayed,
          displayBase,
          compareForDisplay,
          upToPct,
        );
        handledPriceEls.add(pEl);
      });

      // [CUSTOM] Badge highlight « économie panier » aussi sur fiche / modal :
      // produit sans remise propre (1re position) mais qui fait progresser les
      // paliers des autres — même emplacement que le badge de vente (à côté
      // du prix via .badges[data-product-id]).
      // Base = panier + pack UNIQUEMENT (pas les items cochés du bloc
      // additionnel) : cocher un produit offert (CD) ne doit pas gonfler le
      // badge avec la valeur du cadeau — seulement la réduction sur le pack
      // / panier déjà présent si on ajoute CE produit.
      let ctxCartSavings = 0;
      if (
        !fromMode &&
        displayed >= displayBase &&
        firstBadgeTemplateFor(ctx.productId)
      ) {
        ctxCartSavings = cartSavingsFromAdding(
          [...cartUnits, ...ctxPackUnits],
          {
            productId: ctx.productId,
            basePrice: displayBase,
            comparePrice: compareForDisplay,
          },
          cdRules,
        );
      }

      applyTbBadge(
        ctx.productId,
        displayed,
        displayBase,
        compareForDisplay,
        fromMode,
        upToPct,
        ctx.scope,
        ctxCartSavings,
      );

      const unitDisplayed = Math.max(0, Math.round(displayedUnit));
      // [CUSTOM] Pack actif : le prix ATC est laissé à bundle-picker.js —
      // ne pas réécrire le prix unitaire de la fiche sur le bouton.
      if (!isBundlePackActive()) {
        root.querySelectorAll("product-buy-price[data-price]").forEach((bp) => {
          const bpId = getProductIdForBuyPrice(bp);
          if (bpId !== ctx.productId) return;
          // Sticky : copié depuis le bouton ATC après calcul (syncStickyBuyPriceFromAtc).
          if (
            bp.closest("product-sticky-form") ||
            bp.hasAttribute("data-sticky-atc-price")
          )
            return;

          const nested = bp.querySelector(".price[data-base-price]");
          const bpBase = nested
            ? parseInt(nested.dataset.basePrice, 10)
            : parseInt(bp.getAttribute("data-price"), 10);
          if (!Number.isFinite(bpBase) || bpBase <= 0) return;

          applyProductBuyPrice(bp, unitDisplayed, displayBase, compareForDisplay);
        });
      }

      if (ctx.productInfo) {
        const form = ctx.productInfo.querySelector(
          'form[data-type="add-to-cart-form"]',
        );
        if (form) syncStickyBuyPriceFromAtc(form);
      }
    }

    // ── Contextes « page » (fiche + chaque modal ouvert) ────────────────────
    for (const ctx of contexts) processContextPriceEl(ctx);

    // ── Cartes / cart drawer / recos hors contextes ─────────────────────────
    document
      .querySelectorAll(".price[data-base-price][data-product-id]")
      .forEach((el) => {
        if (el.hasAttribute("data-bundle-price")) return;
        if (el.hasAttribute("data-bundle-pack-expand")) return;
        if (handledPriceEls.has(el)) return;
        const pid = parseInt(el.dataset.productId, 10);
        if (!Number.isFinite(pid)) return;
        const basePrice = parseInt(el.dataset.basePrice, 10);
        const comparePrice = parseInt(el.dataset.baseCompareAtPrice, 10) || 0;
        if (!Number.isFinite(basePrice) || basePrice <= 0) return;

        // Cartes hors contexte (grilles, recos, drawer) : prix affiché = coût
        // MARGINAL capé au prix de base. Cohérent avec « Lowest priced first »
        // — si ajouter ce produit déplace une remise sur un produit du panier
        // et que le coût dépasse le prix de base, on cap (pas de balise).
        const fromMode = isFromMode(el);
        let displayBase = basePrice;
        let displayed;
        // [CUSTOM] Base carte = panier + pack collection bundle, sauf carte dans
        // un quick view « œil » (sans data-bundle-controls) → panier seul.
        const cardQv = el.closest ? el.closest("quick-view") : null;
        const cardPackUnits =
          cardQv && !cardQv.hasAttribute("data-bundle-controls")
            ? []
            : bundlePackUnits;
        const cardBaseUnits = cardPackUnits.length
          ? [...cartUnits, ...cardPackUnits]
          : cartUnits;
        const computeCardNet = (bp, cp) => {
          const withP = [
            ...cardBaseUnits,
            { productId: pid, basePrice: bp, comparePrice: cp },
          ];
          return cappedMarginal(cardBaseUnits, withP, bp, cdRules);
        };
        if (fromMode && el.dataset.tbPriceMin) {
          const priceMin = parseInt(el.dataset.tbPriceMin, 10);
          if (Number.isFinite(priceMin) && priceMin > 0) {
            displayBase = priceMin;
            displayed = computeCardNet(priceMin, 0);
          } else {
            displayed = computeCardNet(basePrice, comparePrice);
          }
        } else {
          displayed = computeCardNet(basePrice, comparePrice);
        }

        const baseUpTo = parseFloat(el.dataset.tbBaseUpToPct) || 0;
        const tbPct =
          displayBase > 0 ? ((displayBase - displayed) * 100) / displayBase : 0;
        let upToPct = Math.max(0, Math.round(Math.max(baseUpTo, tbPct)));
        if (fromMode) {
          const variantsMax = computeMaxDiscountAcrossVariants(
            pid,
            cardBaseUnits,
            cdRules,
            el,
          );
          if (variantsMax > upToPct) upToPct = variantsMax;
        }

        const cardScope =
          el.closest(".shopify-section, .quick-view, .x-modal, section") ||
          document;
        applyOutsidePrice(el, displayed, displayBase, comparePrice, upToPct);

        // [CUSTOM] Économie panier si le produit n'a pas de remise propre mais
        // fait progresser les paliers des autres (badge « {économie} PACK »).
        let cardCartSavings = 0;
        if (!fromMode && displayed >= displayBase && firstBadgeTemplateFor(pid)) {
          cardCartSavings = cartSavingsFromAdding(
            cardBaseUnits,
            { productId: pid, basePrice: displayBase, comparePrice },
            cdRules,
          );
        }

        applyTbBadge(
          pid,
          displayed,
          displayBase,
          comparePrice,
          fromMode,
          upToPct,
          cardScope,
          cardCartSavings,
        );
      });

    // ── product-buy-price hors contexte (rare) ──────────────────────────────
    const handledBuyPrices = new Set();
    contexts.forEach((ctx) => {
      const root = ctx.scope === document ? document : ctx.scope;
      root.querySelectorAll("product-buy-price[data-price]").forEach((bp) => {
        const bpId = getProductIdForBuyPrice(bp);
        if (bpId === ctx.productId) handledBuyPrices.add(bp);
      });
    });
    document.querySelectorAll("product-buy-price[data-price]").forEach((el) => {
      if (handledBuyPrices.has(el)) return;
      if (
        el.closest("product-sticky-form") ||
        el.hasAttribute("data-sticky-atc-price")
      )
        return;
      const formId = el.getAttribute("form");
      const form = formId ? document.getElementById(formId) : null;
      // Pack expand : prix ATC géré par bundle-pack-pricing.js
      if (form?.getAttribute("data-bundle-expand") === "true") return;
      const pid = getProductIdForBuyPrice(el);
      if (!Number.isFinite(pid)) return;
      const nested = el.querySelector(".price[data-base-price]");
      const basePrice = nested
        ? parseInt(nested.dataset.basePrice, 10)
        : parseInt(el.getAttribute("data-price"), 10);
      const comparePrice = nested
        ? parseInt(nested.dataset.baseCompareAtPrice, 10) || 0
        : 0;
      if (!Number.isFinite(basePrice) || basePrice <= 0) return;
      const withP = [...cartUnits, { productId: pid, basePrice, comparePrice }];
      const displayed = cappedMarginal(cartUnits, withP, basePrice, cdRules);
      applyProductBuyPrice(el, displayed, basePrice, comparePrice);
    });

    // ── Items du bloc additionnel ──────────────────────────────────────────
    document.querySelectorAll("[data-bundle-price]").forEach((el) => {
      const pid = parseInt(el.dataset.productId, 10);
      if (!Number.isFinite(pid)) return;

      const bundleItem = el.closest("[data-bundle-item]");
      const checkbox = bundleItem?.querySelector('input[name="bundles"]');
      const isChecked = checkbox?.checked === true;

      // Lecture prioritaire depuis le sélecteur de variantes (option courante),
      // qui reflète la SÉLECTION RÉELLE de l'utilisateur — évite la désync
      // affichée si data-current-price n'a pas encore été synchronisé par
      // custom_product-card.js.
      let basePrice = 0;
      let comparePrice = 0;
      const select = bundleItem?.querySelector("[data-bundle-variant-select]");
      const opt = select?.selectedOptions?.[0];
      if (opt) {
        const p = parseInt(opt.dataset.price, 10);
        const c = parseInt(opt.dataset.comparePrice, 10) || 0;
        if (Number.isFinite(p) && p > 0) {
          basePrice = p;
          if (c > p) comparePrice = c;
        }
      }
      if (basePrice <= 0) {
        basePrice =
          parseInt(el.dataset.currentPrice, 10) ||
          parseInt(el.dataset.priceMin, 10) ||
          0;
        comparePrice =
          parseInt(el.dataset.currentComparePrice, 10) ||
          parseInt(el.dataset.comparePrice, 10) ||
          0;
      }
      if (basePrice <= 0) return;

      if (isChecked) {
        // [CUSTOM] Placeholder « sélectionner une variante » (custom_preselect-variant) :
        // tant qu'aucune variante n'est choisie, prix variables → « Dès … » FIGÉ sur
        // le prix affiché avant le cochage (aperçu décoché, remises TB/CD comprises).
        // Le data-price de la checkbox reprend ce prix figé → totaux ATC / sticky
        // cohérents avec l'affichage, jusqu'à la sélection de la variante.
        const pendingPlaceholder =
          !!opt && opt.hasAttribute("data-bundle-variant-placeholder");
        if (pendingPlaceholder) {
          const pendingMin = parseInt(el.dataset.priceMin, 10) || 0;
          const pendingMax = parseInt(el.dataset.priceMax, 10) || 0;
          if (pendingMin > 0 && pendingMax > pendingMin) {
            if (!el.dataset.tbPendingPrice) {
              const prev = parseInt(el.dataset.tbDisplayPrice, 10);
              let frozen;
              if (Number.isFinite(prev) && prev > 0 && prev <= pendingMin) {
                frozen = prev;
              } else {
                // Pas d'aperçu antérieur (ex. précoché au chargement) :
                // même formule que l'aperçu décoché (marginal capé à priceMin)
                const withPending = [
                  ...baseStateUnits,
                  { productId: pid, basePrice: pendingMin, comparePrice: 0 },
                ];
                frozen = cappedMarginal(
                  baseStateUnits,
                  withPending,
                  pendingMin,
                  cdRules,
                  true,
                );
              }
              el.dataset.tbPendingPrice = String(frozen);
              // « Jusqu'à -X% » figé lui aussi au moment du cochage
              // [CUSTOM] Utilise le upToPct réellement affiché avant le cochage
              // (stocké par applyBundlePrice) plutôt que de recalculer —
              // évite les écarts dus à un baseStateUnits différent entre évaluations.
              el.dataset.tbPendingUpTo = el.dataset.tbLastUpToPct || String(
                computeMaxDiscountAcrossVariants(
                  pid,
                  baseStateUnits,
                  cdRules,
                  el,
                  true,
                ),
              );
            }
            applyBundlePrice(
              el,
              parseInt(el.dataset.tbPendingPrice, 10) || pendingMin,
              pendingMin,
              0,
              {
                forceFromMode: true,
                upToPct: parseFloat(el.dataset.tbPendingUpTo) || 0,
              },
            );
            return;
          }
        }
        // Variante choisie (ou prix fixes) : oublier le prix « Dès » figé
        delete el.dataset.tbPendingPrice;
        delete el.dataset.tbPendingUpTo;

        // Coché → on affiche le PRIX SPÉCIFIQUE de la variante (pas « Dès … »),
        // avec badge inline « Epargnez X% » pour chaque variante.
        const qIdx = queueIndexOf(el);
        if (qIdx === -1) enqueueLock(el, pid, basePrice, comparePrice);
        const entry = lockedQueue[queueIndexOf(el)];
        if (entry) {
          applyBundlePrice(
            el,
            mergeCdBundleDisplayPrice(el, basePrice, entry.lockedPrice),
            basePrice,
            comparePrice,
            {
              forceVariantMode: true,
            },
          );
          if (bundleItem) {
            const stateForOpt = buildStateExcludingQueueIndex(
              queueIndexOf(el),
              cartUnits,
              ficheCtx,
            );
            updateBundleVariantSelectForItem(bundleItem, stateForOpt, cdRules);
          }
        }
        return;
      }

      // Non coché : oublier un éventuel prix « Dès » figé (recapturé au prochain cochage)
      delete el.dataset.tbPendingPrice;
      delete el.dataset.tbPendingUpTo;

      // Non coché → on revient à l'affichage GLOBAL.
      //   • Multi-variantes à prix différents (priceMax > priceMin) :
      //     « Dès <marginal pour priceMin> » + « Jusqu'à -X% off »
      //     (X = max parmi toutes les variantes, lu via data-tb-variants).
      //   • Variantes au même prix (priceMax == priceMin), avec prix barrés
      //     différents : prix normal + strikethrough sur le MAX compare_at
      //     parmi les variantes + badge inline « Epargnez X% ».
      const priceMin = parseInt(el.dataset.priceMin, 10) || basePrice;
      const priceMax = parseInt(el.dataset.priceMax, 10) || priceMin;
      const isVariantRange = priceMax > priceMin;

      // Calcule maxCompareAt = max(compare_at) parmi les variantes où
      // compare_at > price (sinon 0).
      const variants = getProductVariants(pid, el);
      let maxCompareAt = 0;
      if (variants) {
        for (const v of variants) {
          const vp = parseInt(v.price, 10) || 0;
          const vc = parseInt(v.compare_at_price, 10) || 0;
          if (vc > vp && vc > maxCompareAt) maxCompareAt = vc;
        }
      }

      // Pour le « Jusqu'à -X% » des bundle items non cochés, on calcule le max
      // contre l'ÉTAT COMPLET (panier + page + items cochés) — pas seulement
      // le panier — pour que la valeur reflète bien la remise que la variante
      // déclencherait DANS LE CONTEXTE ACTUEL de la fiche.
      const variantsMax = computeMaxDiscountAcrossVariants(
        pid,
        baseStateUnits,
        cdRules,
        el,
        true,
      );

      // Prix preview = coût MARGINAL capé au prix de base. Inclut la perte de
      // remise sur un item plus cher déplacé : si l'ajout coûte plus que le
      // prix de base, on cap au prix de base et l'item s'affiche sans balise
      // de réduction (cohérent avec les items cochés).
      if (isVariantRange) {
        const withQ = [
          ...baseStateUnits,
          { productId: pid, basePrice: priceMin, comparePrice: 0 },
        ];
        const net = cappedMarginal(
          baseStateUnits,
          withQ,
          priceMin,
          cdRules,
          true,
        );
        applyBundlePrice(
          el,
          mergeCdBundleDisplayPrice(el, priceMin, net),
          priceMin,
          0,
          { upToPct: variantsMax },
        );
      } else {
        const refCompare = maxCompareAt > basePrice ? maxCompareAt : 0;
        const withQ = [
          ...baseStateUnits,
          { productId: pid, basePrice, comparePrice: refCompare },
        ];
        const net = cappedMarginal(
          baseStateUnits,
          withQ,
          basePrice,
          cdRules,
          true,
        );
        applyBundlePrice(
          el,
          mergeCdBundleDisplayPrice(el, basePrice, net),
          basePrice,
          refCompare,
        );
      }

      if (bundleItem) {
        updateBundleVariantSelectForItem(bundleItem, baseStateUnits, cdRules);
      }
    });

    // [CUSTOM] Affichage progressif dans le cart drawer (badges position + %,
    // ligne d'économie totale).
    applyCartDrawerProgressive();

    // [CUSTOM] Badge « cadeau » (gifts_badge), panier réel, sitewide.
    applyGiftBadges(cartUnits);

    refireBundleChange();
  }

  // ─── Cart drawer : affichage progressif ─────────────────────────────────────
  //
  // Quand au moins une règle est progressive, on remplace dans les lignes du
  // panier les badges d'économie (devise) par : badge--highlight « Nᵉ produit » +
  // badge--onsale « -X% » (position, calculée sur les produits du panier
  // éligibles classés par prix), et on affiche l'économie totale en badge à
  // droite du titre highlight du groupe. Les prix restent ceux rendus par
  // Shopify (réductions réelles) ; seuls les badges sont réécrits.

  function ordinalFrTb(n) {
    return n === 1 ? "1er" : n + "ᵉ";
  }

  function progressivePositionLabel(pos, lastQty) {
    if (lastQty > 0 && pos >= lastQty) return ordinalFrTb(lastQty) + "+ produit";
    return ordinalFrTb(pos) + " produit";
  }

  function applyCartDrawerProgressive() {
    const progRules = tbRules.filter((r) => r.progressive);
    if (!progRules.length) {
      // Même sans progressif : appliquer le tri catégories + prix unitaire.
      document.querySelectorAll("cart-items").forEach((container) => {
        orderCartDrawerRoot(container);
      });
      return;
    }

    // Paliers fusionnés (comme le bundle) + ensemble des produits éligibles.
    const tierMap = {};
    const progIds = new Set();
    progRules.forEach((r) => {
      r.tiers.forEach((t) => {
        if (!(t.qty in tierMap) || t.pct > tierMap[t.qty]) tierMap[t.qty] = t.pct;
      });
      r.productIdSet.forEach((id) => progIds.add(id));
    });
    const mergedTiers = Object.keys(tierMap)
      .map((q) => ({ qty: parseInt(q, 10), pct: tierMap[q] }))
      .sort((a, b) => a.qty - b.qty);
    const lastQty = mergedTiers.length
      ? mergedTiers[mergedTiers.length - 1].qty
      : 0;

    document.querySelectorAll("cart-items").forEach((container) => {
      processCartItemsProgressive(container, progRules, progIds, mergedTiers, lastQty);
    });
  }

  function processCartItemsProgressive(container, progRules, progIds, mergedTiers, lastQty) {
    // Repart d'un état propre : dégrouper nos wrappers progressifs, retire les
    // clones + la ligne d'économie (les originaux restent en place).
    unwrapProgressiveCartGroups(container);
    container
      .querySelectorAll("[data-tb-clone], [data-tb-cart-saved]")
      .forEach((el) => el.remove());

    const originalLines = Array.from(
      container.querySelectorAll(
        "li[data-product-id][data-price]:not([data-tb-clone])",
      ),
    );

    // Unités éligibles (toutes règles confondues) → tri par PRIX UNITAIRE
    // décroissant (pas par ligne) → position + % du palier de la position.
    // basePrice/disc/isFixed : requis par allocateGifts (cf. plus bas), qui
    // réutilise exactement la même fonction que le moteur de prix principal.
    const units = [];
    originalLines.forEach((li) => {
      const pid = parseInt(li.dataset.productId, 10);
      if (!progIds.has(pid)) return;
      const price = parseInt(li.dataset.price, 10) || 0;
      const qty = parseInt(li.dataset.quantity, 10) || 1;
      for (let i = 0; i < qty; i++) {
        units.push({ li, price, basePrice: price, productId: pid, disc: 0, isFixed: false });
      }
    });
    if (!units.length) {
      container.querySelectorAll("[data-tb-cart-saved]").forEach((el) => el.remove());
      orderCartDrawerRoot(container);
      return;
    }
    units.sort((a, b) => b.price - a.price);
    units.forEach((u, idx) => {
      u.position = idx + 1;
    });

    // [CUSTOM] Cadeaux des paliers franchis (100 % de remise), AVANT le % de
    // position — une unité déjà offerte ne doit pas en plus recevoir/consommer
    // un % de palier (même règle que le moteur de prix principal, cf.
    // computeUnitDiscounts). qty par règle = nb d'unités éligibles à CETTE règle.
    progRules.forEach((rule) => {
      const ruleQty = units.reduce(
        (n, u) => n + (rule.productIdSet.has(u.productId) ? 1 : 0),
        0,
      );
      allocateGifts(units, rule, ruleQty);
    });
    units.forEach((u) => {
      u.pct = u.disc === 100 ? 100 : tierPctFor(u.position, mergedTiers);
    });

    // Groupes par (ligne, label) : les unités du DERNIER palier (« Nᵉ+ »)
    // partagent le même label → une seule ligne (regroupées) ; les paliers
    // distincts (« 2ᵉ », « 3ᵉ »…) restent sur des lignes séparées. Les cadeaux
    // (pct 100) portent le label « Cadeau » plutôt qu'une position.
    const perLine = new Map(); // li -> [{ label, pct, count, minPos, price }]
    units.forEach((u) => {
      const label =
        u.pct >= 100 ? null : progressivePositionLabel(u.position, lastQty);
      if (!perLine.has(u.li)) perLine.set(u.li, []);
      const groups = perLine.get(u.li);
      let g = groups.find((x) => x.label === label && x.pct === u.pct);
      if (!g) {
        g = { label, pct: u.pct, count: 0, minPos: u.position, price: u.price };
        groups.push(g);
      }
      g.count++;
      g.minPos = Math.min(g.minPos, u.position);
    });

    // [CUSTOM] UNE LIGNE PAR GROUPE (ligne panier + % + label) : deux paliers
    // différents ne sont JAMAIS fusionnés sur la même ligne. Seules les unités
    // de même SKU + même % + même label sont regroupées (typiquement le dernier
    // palier illimité « Nᵉ+ »), auquel cas la ligne porte leur quantité.
    //
    // IMPORTANT — ne JAMAIS déplacer les <li> d'origine dans le DOM : déplacer
    // un <quantity-input> le reconnecte et (bug natif du thème sur `signal`)
    // empile un listener clic → un clic « + » ajoute N unités. On insère les
    // clones à côté de l'original et on trie via CSS `order`.
    // Exception contrôlée plus bas : wrapProgressiveCartGroups (signal corrigé).
    perLine.forEach((groups, li) => {
      groups.sort((a, b) => a.minPos - b.minPos);
      const lineQty = groups.reduce((n, g) => n + g.count, 0);
      const isSplit = groups.length > 1;
      let prevEl = null;
      groups.forEach((g, i) => {
        const el = i === 0 ? li : buildCartSplitRow(li);
        if (i > 0 && prevEl) prevEl.insertAdjacentElement("afterend", el);
        renderCartSplitRow(el, g, lastQty, isSplit, lineQty);
        el.style.order = String(g.minPos);
        prevEl = el;
      });
    });

    // Regroupe tous les produits d'une même règle progressive (barre + titre
    // = palier le plus haut franchi uniquement).
    wrapProgressiveCartGroups(container, progRules, units);
    // [CUSTOM] Tri racine du drawer : catégories + prix unitaire hors réduction.
    // Ne PAS utiliser la position progressive (sinon les hors-groupe restent
    // à order:0 et passent devant le pack).
    orderCartDrawerRoot(container);
  }

  // Palier le plus haut franchi pour une quantité d'unités éligibles.
  function highestReachedTier(tiers, unitCount) {
    let best = null;
    for (const t of tiers) {
      if (t.qty <= unitCount) best = t;
    }
    return best;
  }

  function buildProgOfferHtml(tier) {
    if (!tier) return "";
    const offer = tier.offer ? formatTbBbCode(tier.offer) : "";
    const linkUrl = tier.linkUrl || "";
    const linkText = tier.linkText || linkUrl;
    if (!offer && !linkUrl) return "";
    let html = `<div class="cart-discount-group__offer text-xs">`;
    if (offer) {
      html += `<span class="cart-discount-group__offer-text font-medium">${offer}</span>`;
    }
    if (linkUrl) {
      html +=
        `<a class="cart-discount-group__offer-link link text-2xs" href="${escapeTbHtml(linkUrl)}">${escapeTbHtml(linkText)}</a>`;
    }
    html += `</div>`;
    return html;
  }

  function escapeTbHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  // [CUSTOM] Même balises que le parseur BBCode global (custom.js) /
  // highlighted-text.liquid. Nécessaire ici car le mode progressif reconstruit
  // le titre/offre du groupe APRÈS cart:updated — trop tard pour le parseur.
  function formatTbBbCode(str) {
    return escapeTbHtml(str)
      .replace(/\[b\]/g, "<strong>")
      .replace(/\[\/b\]/g, "</strong>")
      .replace(/\[i\]/g, "<em>")
      .replace(/\[\/i\]/g, "</em>")
      .replace(/\[u\]/g, "<u>")
      .replace(/\[\/u\]/g, "</u>");
  }

  function stripTbBbCode(str) {
    return String(str).replace(/\[\/?[biu]\]/g, "");
  }

  // Sort les lignes hors de nos wrappers progressifs (avant rebuild).
  function unwrapProgressiveCartGroups(container) {
    const root = container.querySelector(".horizontal-products");
    if (!root) return;
    root.querySelectorAll("[data-tb-prog-group]").forEach((group) => {
      const items = group.querySelector(".cart-discount-group__items");
      if (items) {
        while (items.firstChild) {
          root.insertBefore(items.firstChild, group);
        }
      }
      group.remove();
    });
  }

  // [CUSTOM] Mode progressif : tous les produits d'une même règle tiered_bundle
  // sont réunis dans un cart-discount-group (barre verticale). Titre highlight =
  // discount_display_name du palier le PLUS HAUT franchi (pas la concaténation
  // de tous les paliers actifs côté Shopify).
  function wrapProgressiveCartGroups(container, progRules, units) {
    const root = container.querySelector(".horizontal-products");
    if (!root) return;

    progRules.forEach((rule) => {
      const ruleUnits = units.filter((u) => rule.productIdSet.has(u.productId));
      if (!ruleUnits.length) return;

      const els = Array.from(
        root.querySelectorAll("li.horizontal-product[data-product-id]"),
      ).filter((el) => {
        if (!rule.productIdSet.has(parseInt(el.dataset.productId, 10))) {
          return false;
        }
        // Déjà placé dans un autre groupe progressif de cette passe.
        return !el.closest("[data-tb-prog-group]");
      });
      // Un seul article / une seule ligne visuelle : pas de groupe.
      if (els.length < 2) return;

      // Sortir les lignes des groupes Liquid (titre souvent concaténé) pour
      // reconstruire un groupe propre par règle.
      els.forEach((el) => {
        const existing = el.closest(".cart-discount-group");
        if (
          !existing ||
          existing.hasAttribute("data-tb-prog-group") ||
          existing.parentNode !== root
        ) {
          return;
        }
        const items = existing.querySelector(".cart-discount-group__items");
        if (items && items.contains(el)) {
          root.insertBefore(el, existing);
        }
      });
      root.querySelectorAll(":scope > .cart-discount-group").forEach((g) => {
        if (g.hasAttribute("data-tb-prog-group")) return;
        const items = g.querySelector(".cart-discount-group__items");
        if (!items || !items.querySelector(".horizontal-product")) g.remove();
      });

      els.sort(
        (a, b) =>
          (parseInt(a.style.order, 10) || 0) - (parseInt(b.style.order, 10) || 0),
      );

      const highest = highestReachedTier(rule.tiers, ruleUnits.length);
      const title = highest
        ? highest.discountDisplayName || highest.discountName || ""
        : "";
      // L'offre est lue sur le palier ATTEINT (comme tiered-bundle-offer.liquid via
      // discount_name) : chaque palier décrit l'upsell depuis son seuil (« ajoute un
      // Nᵉ… »), pas le palier suivant.
      const offerHtml = buildProgOfferHtml(highest);
      const ruleSavings = ruleUnits.reduce(
        (sum, u) => sum + Math.round((u.price * u.pct) / 100),
        0,
      );

      const group = document.createElement("li");
      group.className = "cart-discount-group";
      group.setAttribute("data-tb-prog-group", "");
      group.setAttribute("role", "group");
      if (title) group.setAttribute("aria-label", stripTbBbCode(title));

      let html = "";
      if (title) {
        html +=
          `<div class="cart-discount-group__title flex items-center gap-1d5 flex-wrap">` +
          `<span class="badge badge--highlight flex items-center gap-1d5 font-medium leading-none">${formatTbBbCode(title)}</span>`;
        if (ruleSavings > 0) {
          html +=
            `<span class="badge badge--onsale flex items-center gap-1d5 font-medium leading-none" data-tb-cart-saved-badge>-${formatMoney(ruleSavings)}</span>`;
        }
        html += `</div>`;
      }
      html += `<div class="cart-discount-group__body">`;
      html += offerHtml;
      html += `<ul class="cart-discount-group__items" role="list"></ul></div>`;
      group.innerHTML = html;

      const itemsUl = group.querySelector(".cart-discount-group__items");
      root.insertBefore(group, els[0]);
      els.forEach((el) => itemsUl.appendChild(el));
    });
  }

  // [CUSTOM] Tri visuel racine du cart drawer (CSS order, sans déplacer le DOM) :
  // 1) packs composés  2) packs bundle Shopify  3) produits individuels
  // Dans chaque catégorie : prix unitaire hors réduction (data-price) ↓.
  // Les groupes de réduction / progressifs sont des « individuels » triés sur
  // le max de leurs lignes. La position progressive ne sert qu'À L'INTÉRIEUR
  // du groupe (déjà posée sur chaque <li>).
  function orderCartDrawerRoot(container) {
    const root = container.querySelector(".horizontal-products");
    if (!root) return;
    root.classList.add("tb-cart-ordered");

    const KIND_RANK = { composed: 0, pack: 1, item: 2 };

    function lineKind(el) {
      const k = el && el.dataset && el.dataset.cartKind;
      return k === "composed" || k === "pack" ? k : "item";
    }

    function linePrice(el) {
      return parseInt(el && el.dataset && el.dataset.price, 10) || 0;
    }

    function entryForChild(el) {
      if (el.classList && el.classList.contains("cart-discount-group")) {
        let price = 0;
        let rank = KIND_RANK.item;
        el.querySelectorAll("li.horizontal-product[data-price]").forEach((p) => {
          const pr = linePrice(p);
          if (pr > price) price = pr;
          const kr = KIND_RANK[lineKind(p)] ?? KIND_RANK.item;
          if (kr < rank) rank = kr;
        });
        return { el, rank, price };
      }
      if (el.matches && el.matches("li.horizontal-product")) {
        return { el, rank: KIND_RANK[lineKind(el)] ?? KIND_RANK.item, price: linePrice(el) };
      }
      return { el, rank: 99, price: 0 };
    }

    const entries = Array.from(root.children).map(entryForChild);
    entries.sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      if (b.price !== a.price) return b.price - a.price;
      return 0;
    });
    entries.forEach((e, i) => {
      e.el.style.order = String(i);
    });
  }

  // Ligne supplémentaire (même ligne panier, autre palier). Les <img is="lazy-image">
  // clonés relancent leur chargement indéfiniment : on fige donc la source
  // courante et on retire le comportement lazy sur la copie.
  function buildCartSplitRow(li) {
    const clone = li.cloneNode(true);
    clone.removeAttribute("id");
    clone.setAttribute("data-tb-clone", "");
    clone.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));
    clone.querySelectorAll("img").forEach((img) => {
      const src = img.currentSrc || img.src;
      img.removeAttribute("is");
      img.removeAttribute("loading");
      img.removeAttribute("data-src");
      img.removeAttribute("srcset");
      if (src) img.setAttribute("src", src);
      img.classList.remove("lazy-image", "loading");
    });
    return clone;
  }

  // Rend une ligne = un palier : prix unitaire remisé, quantité du groupe, et
  // ses balises (1 balise → inline à droite du prix ; 2 → en dessous).
  //
  // Le sélecteur de quantité reste le composant natif (flèches incluses) : sa
  // valeur affiche la quantité DU GROUPE, et data-tb-* permet de retraduire une
  // saisie en quantité absolue de la ligne panier (cf. le listener plus bas).
  // lineQty = quantité Shopify réelle de la ligne (somme des groupes du SKU).
  function renderCartSplitRow(el, g, lastQty, isSplit, lineQty) {
    // Chaque rangée garde ses contrôles natifs (flèches + « Retirer ») et
    // affiche la quantité DE SON PALIER. Comme toutes les rangées d'un même SKU
    // pointent sur la même ligne de panier Shopify, les interactions sont
    // traduites en DELTA sur cette ligne (cf. les listeners plus bas) : le
    // moteur re-répartit ensuite les unités par palier.
    el.toggleAttribute("data-tb-split", isSplit);
    el.dataset.tbGroupCount = String(g.count);
    el.dataset.tbLineQty = String(
      isSplit
        ? Number.isFinite(lineQty) && lineQty > 0
          ? lineQty
          : parseInt(el.dataset.quantity, 10) || g.count
        : g.count,
    );

    const qtyInput = el.querySelector(
      '.horizontal-product__quantity input[name="updates[]"]',
    );
    if (qtyInput && isSplit) qtyInput.value = String(g.count);

    const priceDiv = el.querySelector(".price");
    el.querySelectorAll("ul.discounts, [data-tb-cart-discounts]").forEach((u) =>
      u.remove(),
    );

    const isGift = g.pct >= 100;
    const reduced = Math.round((g.price * (100 - g.pct)) / 100);
    const label = isGift
      ? g.count > 1
        ? g.count + " cadeaux"
        : "Cadeau"
      : progressivePositionLabel(g.minPos, lastQty);
    const highlight = `<span class="badge badge--highlight flex items-center gap-1d5 font-medium leading-none">${label}</span>`;
    // Cadeau : highlight seul (« -100% » n'a pas de sens pour un produit offert).
    const badgesHtml = isGift
      ? highlight
      : highlight +
        `<span class="badge badge--onsale flex items-center gap-1d5 font-medium leading-none">-${g.pct}%</span>`;
    const inline = isGift;

    if (priceDiv) {
      priceDiv.classList.toggle("price--on-sale", g.pct > 0);
      priceDiv.innerHTML =
        g.pct > 0
          ? `<span class="price__regular whitespace-nowrap">${regularPriceText(reduced)}</span>` +
            `<span class="price__sale inline-flex items-center relative">${formatMoney(g.price)}</span>` +
            (inline ? badgesHtml : "")
          : formatMoney(g.price);
    }

    if (g.pct <= 0 || inline) return;

    const ul = document.createElement("ul");
    ul.className = "discounts grid gap-2";
    ul.setAttribute("data-tb-cart-discounts", "");
    const item = document.createElement("li");
    item.className =
      "discounts__discount text-xs flex flex-wrap items-center gap-1";
    item.innerHTML = badgesHtml;
    ul.appendChild(item);
    if (priceDiv && priceDiv.parentNode) {
      priceDiv.insertAdjacentElement("afterend", ul);
    } else {
      el.appendChild(ul);
    }
  }

  // ─── Lignes éclatées : interactions traduites en delta ──────────────────────
  //
  // Toutes les rangées d'un même SKU partagent la ligne de panier Shopify (une
  // variante = une ligne). Une saisie sur une rangée vaut donc pour SON palier
  // seulement : on convertit en quantité absolue de la ligne, puis on laisse le
  // moteur reclasser les unités par palier au re-rendu.

  function splitRowContext(el) {
    const row = el.closest("[data-tb-split]");
    if (!row) return null;
    const cartItems = row.closest("cart-items");
    const groupCount = parseInt(row.dataset.tbGroupCount, 10);
    const lineQty = parseInt(row.dataset.tbLineQty, 10);
    if (!cartItems || !Number.isFinite(groupCount) || !Number.isFinite(lineQty)) {
      return null;
    }
    return { row, cartItems, groupCount, lineQty };
  }

  // Applique une quantité absolue à la ligne panier d'une rangée éclatée.
  function splitRowUpdate(ctx, el, absolute) {
    const index = el.getAttribute("data-index");
    if (!index) return;
    ctx.cartItems.updateQuantity(index, Math.max(0, absolute), null, el);
  }

  // Flèches : un clic = exactement une unité en plus / en moins sur le SKU.
  // Interception AVANT QuantityInput (qui ferait stepUp/stepDown sur la valeur
  // affichée, laquelle ne représente que le palier, pas la ligne panier).
  document.addEventListener(
    "click",
    (e) => {
      const button =
        e.target.closest && e.target.closest(".quantity__button[name]");
      if (!button) return;
      const ctx = splitRowContext(button);
      if (!ctx) return;
      const input = ctx.row.querySelector('input[name="updates[]"]');
      if (!input) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const step = button.getAttribute("name") === "plus" ? 1 : -1;
      splitRowUpdate(ctx, input, ctx.lineQty + step);
    },
    true,
  );

  // Saisie directe : on n'ajoute/retire que l'écart avec le nombre AFFICHÉ sur
  // la rangée (sa quantité de palier), pas la quantité réelle de la ligne.
  document.addEventListener(
    "change",
    (e) => {
      const input = e.target;
      if (!input.matches || !input.matches('input[name="updates[]"]')) return;
      const ctx = splitRowContext(input);
      if (!ctx) return;
      const value = parseInt(input.value, 10);
      if (!Number.isFinite(value)) return;
      e.stopImmediatePropagation();
      splitRowUpdate(ctx, input, ctx.lineQty - ctx.groupCount + value);
    },
    true,
  );

  // « Retirer » : retire seulement les unités AFFICHÉES sur cette rangée.
  document.addEventListener(
    "click",
    (e) => {
      const link =
        e.target.closest && e.target.closest("[is='cart-remove-button']");
      if (!link) return;
      const ctx = splitRowContext(link);
      if (!ctx) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      splitRowUpdate(ctx, link, ctx.lineQty - ctx.groupCount);
    },
    true,
  );

  // Flag pour distinguer un bundle:change émis par TB d'un émis par d'autres
  // scripts (theme.js ProductBundleDetails, custom_preselect-variant…). Permet
  // au listener bundle:change de re-évaluer sur dispatch externe sans boucler
  // sur les nôtres.
  let dispatchingBundleChange = false;
  function refireBundleChange() {
    dispatchingBundleChange = true;
    try {
      document
        .querySelectorAll('[data-type="add-to-cart-form"]')
        .forEach((form) => {
          const scope = form.closest(".shopify-section, .quick-view, .x-modal");
          const checkedBundles = scope
            ? Array.from(
                scope.querySelectorAll('input[name="bundles"]:checked'),
              )
            : [];
          form.dispatchEvent(
            new CustomEvent("bundle:change", {
              detail: {
                items: checkedBundles.map((input) => ({
                  id: input.value,
                  quantity: 1,
                  price: input.getAttribute("data-price"),
                })),
              },
            }),
          );
        });
    } finally {
      dispatchingBundleChange = false;
    }
    syncAllStickyBuyPricesFromAtc();
  }

  // ─── Détection de variante depuis le DOM (pour le pré-rendu sans flash) ────

  function detectVariantInScope(productInfo) {
    if (!productInfo || !hasCompleteVariantSelection(productInfo)) return null;
    const variantPicker = productInfo.querySelector("variant-picker");
    if (!variantPicker) return null;

    const totalOptions = getVariantPickerOptionCount(variantPicker);
    const checkedInputs = Array.from(
      variantPicker.querySelectorAll('input[type="radio"]:checked'),
    );
    if (totalOptions > 0 && checkedInputs.length < totalOptions) return null;

    const selectedOptions = checkedInputs.map((inp) => inp.value);
    const selects = Array.from(variantPicker.querySelectorAll("select"));
    selects.forEach((sel) => {
      if (sel.value !== "" && sel.value != null)
        selectedOptions.push(sel.value);
    });
    if (selectedOptions.length === 0) return null;

    const scriptTag = productInfo.querySelector(
      "script[data-product-variant-data]",
    );
    if (!scriptTag) return null;
    let data;
    try {
      data = JSON.parse(scriptTag.textContent);
    } catch (e) {
      return null;
    }
    const variants = data.productVariants;
    if (!Array.isArray(variants)) return null;
    return (
      variants.find(
        (v) =>
          v.options &&
          v.options.length === selectedOptions.length &&
          selectedOptions.every((opt, i) => v.options[i] === opt),
      ) || null
    );
  }

  // Pré-mise à jour des data-* prix quand une variante COMPLÈTE est sélectionnée
  // (optionValueSelectionChange, avant fetch theme.js). Le badge est recalculé
  // par evaluateAndApply (scope modal/fiche + TB marginal).
  function handleOptionValueSelection(payload) {
    const eventTarget =
      payload?.data?.event?.target || payload?.data?.target || null;
    const clickedInfo = eventTarget?.closest?.("product-info[data-product-id]");
    if (!clickedInfo) {
      evaluateAndApply();
      return;
    }

    if (shouldShowVariantPreview(clickedInfo)) {
      evaluateAndApply();
      return;
    }

    const variant = detectVariantInScope(clickedInfo);
    if (!variant) {
      evaluateAndApply();
      return;
    }

    const pid = parseInt(clickedInfo.dataset.productId, 10);
    if (!Number.isFinite(pid)) {
      evaluateAndApply();
      return;
    }

    const scope = getScopeForProductInfo(clickedInfo);
    const root = scope === document ? document : scope;
    root
      .querySelectorAll(
        `.price[data-product-id="${pid}"][data-base-price]:not([data-bundle-price]):not([data-bundle-pack-expand])`,
      )
      .forEach((priceEl) => {
        if (variant.price != null)
          priceEl.dataset.basePrice = String(variant.price);
        priceEl.dataset.baseCompareAtPrice = String(
          variant.compare_at_price || 0,
        );
        priceEl.dataset.tbVariantSelected = "true";
        priceEl
          .querySelectorAll(".price__regular--from")
          .forEach((s) => s.remove());
      });

    evaluateAndApply();
  }

  // ─── Coche / décoche / variante ─────────────────────────────────────────────

  function handleCheckboxChange(cb) {
    const bundleItem = cb.closest("[data-bundle-item]");
    const priceEl = bundleItem?.querySelector("[data-bundle-price]");
    if (!priceEl || !bundleItem) return;
    const pid = parseInt(bundleItem.dataset.productId, 10);
    if (!Number.isFinite(pid)) return;

    // Lecture prioritaire : sélecteur de variante (option courante), pour
    // éviter le décalage si data-current-price n'a pas encore été mis à jour
    // par custom_product-card.js's applySelection.
    let basePrice = 0;
    let comparePrice = 0;
    const select = bundleItem.querySelector("[data-bundle-variant-select]");
    const opt = select?.selectedOptions?.[0];
    if (opt) {
      const p = parseInt(opt.dataset.price, 10);
      const c = parseInt(opt.dataset.comparePrice, 10) || 0;
      if (Number.isFinite(p) && p > 0) {
        basePrice = p;
        if (c > p) comparePrice = c;
      }
    }
    if (basePrice <= 0) {
      basePrice =
        parseInt(priceEl.dataset.currentPrice, 10) ||
        parseInt(priceEl.dataset.priceMin, 10) ||
        0;
      comparePrice =
        parseInt(priceEl.dataset.currentComparePrice, 10) ||
        parseInt(priceEl.dataset.comparePrice, 10) ||
        0;
    }

    if (cb.checked) enqueueLock(priceEl, pid, basePrice, comparePrice);
    else dequeueLock(priceEl);
  }

  function handleBundleVariantChange(select) {
    const bundleItem = select.closest("[data-bundle-item]");
    const priceEl = bundleItem?.querySelector("[data-bundle-price]");
    if (!priceEl) return;
    // On lit le prix de la variante DIRECTEMENT depuis l'option sélectionnée
    // du <select> (data-price), pas via data-current-price qui peut être
    // encore stale au moment où ce capture handler tourne (custom_product-card.js
    // applySelection ne s'est pas encore exécutée).
    const opt = select.selectedOptions?.[0];
    const newBase = parseInt(opt?.dataset?.price, 10);
    let newCompare = parseInt(opt?.dataset?.comparePrice, 10) || 0;
    if (newCompare && newCompare <= newBase) newCompare = 0;
    if (!Number.isFinite(newBase) || newBase <= 0) return;
    const idx = queueIndexOf(priceEl);
    if (idx !== -1) updateQueueEntry(priceEl, newBase, newCompare);
  }

  // ─── Init / événements ───────────────────────────────────────────────────────

  async function fetchCart() {
    try {
      const res = await fetch("/cart.js");
      const data = await res.json();
      if (data && Array.isArray(data.items)) {
        cartData = {
          items: data.items.map((it) => ({
            product_id: it.product_id,
            quantity: it.quantity,
            price: it.original_price ?? it.price ?? 0,
            compare_at_price:
              it.compare_at_price ?? it.variant?.compare_at_price ?? 0,
          })),
        };
      }
    } catch (_) {}
  }

  // Debounce simple pour éviter les rafales d'eval (modal init, etc.)
  let scheduled = false;
  function scheduleEval() {
    if (scheduled) return;
    scheduled = true;
    Promise.resolve().then(() => {
      scheduled = false;
      evaluateAndApply();
    });
  }

  // [CUSTOM] Point d'entrée public pour bundle-picker.js : à la sélection d'un
  // pack, le radio du pack n'est capté par aucun listener ci-dessous (ils visent
  // variant-picker / [data-bundle-item]) et le bundle:change émis par CD ne bulle
  // pas jusqu'au document → le recalcul tiered n'était déclenché qu'au hasard
  // d'un autre évènement (mise à jour aléatoire/différée). bundle-picker appelle
  // donc ceci explicitement après avoir mis à jour window.__bundlePackCD.
  window.__tieredBundlePricing = { applyAll: scheduleEval };

  function applyCartUpdate(cart) {
    if (cart && Array.isArray(cart.items)) {
      cartData = {
        items: cart.items.map((it) => ({
          product_id: it.product_id,
          quantity: it.quantity,
          price: it.original_price ?? it.price ?? 0,
          compare_at_price:
            it.compare_at_price ?? it.variant?.compare_at_price ?? 0,
        })),
      };
    }
    // ProductBundleDetails décoche les bundles sur cartUpdate (source product-form)
    // de façon synchrone — on recalcule après pour ne pas compter des coches
    // périmées ni recalculer les prix figés avant le décochage.
    Promise.resolve().then(() => {
      syncLockedQueueWithDom();
      recomputeLockedQueueSequential(0);
      evaluateAndApply();
    });
  }

  function bindEvents() {
    document.addEventListener("cart:updated", (e) => {
      applyCartUpdate(e.detail?.cart);
    });

    if (window.theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
      theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        (payload) => {
          applyCartUpdate(payload?.cart);
        },
      );
    }

    // Option click (AVANT fetch) : pré-mise à jour du .price element avec les
    // données de la variante détectée côté DOM. Élimine le flash entre la
    // variante précédente et la nouvelle.
    if (window.theme?.pubsub?.PUB_SUB_EVENTS?.optionValueSelectionChange) {
      theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
        handleOptionValueSelection,
      );
    }

    // Fallback DOM : si le pubsub n'est pas tiré (theme custom, override, etc.)
    // on capte aussi un change DOM natif dans un variant-picker pour mettre à
    // jour le prix + le badge per-variant.
    document.addEventListener("change", (e) => {
      const picker = e.target?.closest?.("variant-picker");
      if (!picker) return;
      handleOptionValueSelection({
        data: { event: e, target: e.target },
      });
    });

    document.addEventListener(
      "click",
      (e) => {
        const target = e.target;
        if (!target || !target.closest) return;
        const picker = target.closest("variant-picker");
        if (!picker) return;
        scheduleEval();
      },
      true,
    );

    // Quand custom_preselect-variant marque la sélection comme complète
    document.addEventListener(
      "variant-validation:complete",
      () => scheduleEval(),
      true,
    );

    // Variant change (APRÈS fetch + updateSourceFromDestination). Capture :
    // applique TB avant product-buy-price.onBundleChanged (bubble).
    const variantEvalAfterTheme = (event) => {
      const variant = event?.detail?.variant ?? event?.data?.variant;
      const productId = event?.data?.productId;
      if (variant?.price != null) {
        const infos = productId
          ? document.querySelectorAll(
              `product-info[data-product-id="${productId}"]`,
            )
          : event.target?.closest
            ? [event.target.closest("product-info[data-product-id]")].filter(
                Boolean,
              )
            : [];
        const variantPrice = parseInt(variant.price, 10) || 0;
        const variantCompare = parseInt(variant.compare_at_price, 10) || 0;
        infos.forEach((pi) => {
          const pid = parseInt(pi.dataset.productId, 10);
          if (!Number.isFinite(pid)) return;
          const scope = getScopeForProductInfo(pi);
          const root = scope === document ? document : scope;
          root
            .querySelectorAll(
              `.price[data-product-id="${pid}"][data-base-price]:not([data-bundle-price]):not([data-bundle-pack-expand])`,
            )
            .forEach((priceEl) => {
              priceEl.dataset.basePrice = String(variantPrice);
              priceEl.dataset.baseCompareAtPrice = String(variantCompare);
              priceEl.dataset.tbVariantSelected = "true";
            });
        });
      }
      evaluateAndApply();
      scheduleEval();
      setTimeout(scheduleEval, 50);
      setTimeout(scheduleEval, 200);
      setTimeout(scheduleEval, 450);
      requestAnimationFrame(syncAllStickyBuyPricesFromAtc);
      setTimeout(syncAllStickyBuyPricesFromAtc, 50);
      setTimeout(syncAllStickyBuyPricesFromAtc, 200);
    };

    if (window.theme?.pubsub?.PUB_SUB_EVENTS?.variantChange) {
      theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.variantChange,
        variantEvalAfterTheme,
      );
    }
    document.addEventListener("variant:change", variantEvalAfterTheme, true);

    document.addEventListener("bundle:change", () => {
      if (dispatchingBundleChange) return;
      scheduleEval();
      requestAnimationFrame(syncAllStickyBuyPricesFromAtc);
    });

    document.addEventListener("input", (e) => {
      if (
        e.target.matches('input[type="number"]') &&
        e.target.closest("quantity-input")
      ) {
        evaluateAndApply();
      }
    });

    // Capture (avant l'eval microtask) pour gérer la file de locks
    document.addEventListener(
      "change",
      (e) => {
        if (e.target.matches('input[name="bundles"]')) {
          handleCheckboxChange(e.target);
        } else if (e.target.matches("[data-bundle-variant-select]")) {
          handleBundleVariantChange(e.target);
        }
      },
      true,
    );

    document.addEventListener("change", (e) => {
      if (
        e.target.matches('input[name="bundles"]') ||
        e.target.matches("[data-bundle-variant-select]") ||
        (e.target.closest("[data-bundle-item]") &&
          !e.target.matches('input[name="bundles"]'))
      ) {
        scheduleEval();
      }
    });

    // Grille collection rechargée (filtres AJAX, infinite scroll)
    document.addEventListener("collection:reloaded", scheduleEval);

    // Contenus dynamiques / modals
    document.addEventListener("recentlyViewed:loaded", scheduleEval, true);
    document.addEventListener("recommendations:loaded", scheduleEval, true);
    document.addEventListener("quickview:loaded", scheduleEval);
    document.addEventListener("quickview:open", scheduleEval);
    document.addEventListener("modal:afterShow", scheduleEval);
    document.addEventListener("bundles:initialized", scheduleEval);
  }

  function init() {
    document
      .querySelectorAll('[data-bundle-item] input[name="bundles"]:checked')
      .forEach((cb) => handleCheckboxChange(cb));
    evaluateAndApply();
    bindEvents();
    fetchCart().then(() => {
      recomputeLockedQueueSequential(0);
      evaluateAndApply();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
